/**
 * @page AufnahmenPage
 * @description Video-Modul — 5-Tab-Seite: Archiv, Kameras, Live, Analyse, Onboarding.
 * Phase 1: Aufnahmen-Archiv mit Filtern, Video-Player, Kamera-Verwaltung, Live-Status.
 * Phase 2+3: Platzhalter (Analyse, Onboarding).
 * @route /aufnahmen
 * @roles admin, ops_manager
 * @i18n recording.*
 * @endpoints GET /api/recordings, /api/recordings/stats, /api/recordings/cameras, etc.
 * @since v2.152.0
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader, KpiCard } from '../components/ui';
import {
 Video, HardDrive, Server, Search, X, Play, Download, Maximize2,
 Plus, Pencil, Trash2, Wifi, WifiOff, Cpu, Database, Upload,
 BarChart3, GraduationCap, Camera, CameraOff, RefreshCw, ChevronLeft, ChevronRight,
 Eye, EyeOff, Settings, AlertTriangle, Info, KeyRound, Zap, TrendingUp,
 CheckCircle2, XCircle, Clock, Target, Loader2, FileVideo, Filter,
 CircleDot, Square, Image, Copy, Link, Share2, Globe,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, CartesianGrid, ReferenceLine } from 'recharts';

class AufnahmenErrorBoundary extends React.Component {
 constructor(props) { super(props); this.state = { hasError: false, error: null, info: null }; }
 static getDerivedStateFromError(error) { return { hasError: true, error }; }
 componentDidCatch(error, info) { this.setState({ info }); console.error('[AufnahmenPage] CRASH:', error, info); }
 render() {
 if (this.state.hasError) return (
 <div className="p-8 text-red-600 font-mono text-sm">
 <h2 className="font-bold text-lg mb-2">AufnahmenPage Error</h2>
 <pre className="whitespace-pre-wrap mb-4">{this.state.error?.toString()}</pre>
 <details open>
 <summary className="cursor-pointer font-bold text-xs mb-2">Stack Trace</summary>
 <pre className="text-[10px] text-red-500 whitespace-pre-wrap overflow-x-auto">{this.state.error?.stack}</pre>
 </details>
 {this.state.info?.componentStack && (
 <details open className="mt-4">
 <summary className="cursor-pointer font-bold text-xs mb-2">Component Stack</summary>
 <pre className="text-[10px] text-red-400 whitespace-pre-wrap">{this.state.info.componentStack}</pre>
 </details>
 )}
 </div>
 );
 return this.props.children;
 }
}

const API = import.meta.env.VITE_API_URL || '';
const TABS = ['archiv', 'cameras', 'live', 'analyse', 'onboarding', 'settings'];
const KATEGORIEN = ['pack', 'inbound', 'retoure', 'referenz'];
const STATUS_LIST = ['available', 'recording', 'uploading', 'error', 'expired'];
const STANDORT_TYPEN = ['packtisch', 'wareneingang', 'retoure', 'versand'];
const STREAM_TYPES = ['sub', 'main'];

// ── Helpers ──────────────────────────────────────────────────────

function formatDuration(sek) {
 if (!sek) return '—';
 const m = Math.floor(sek / 60);
 const s = sek % 60;
 return `${m}:${String(s).padStart(2, '0')}`;
}

function formatSize(mb) {
 if (!mb) return '—';
 if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
 return `${Number(mb).toFixed(1)} MB`;
}

function formatStorageGB(gb) {
 if (!gb) return '0 GB';
 if (gb >= 1024) return `${(gb / 1024).toFixed(1)} TB`;
 return `${Number(gb).toFixed(1)} GB`;
}

function timeAgo(dateStr) {
 if (!dateStr) return '—';
 const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
 if (diff < 60) return `${Math.round(diff)}s`;
 if (diff < 3600) return `${Math.round(diff / 60)}m`;
 if (diff < 86400) return `${Math.round(diff / 3600)}h`;
 return new Date(dateStr).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatDate(dateStr) {
 if (!dateStr) return '—';
 return new Date(dateStr).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusColor(status) {
 switch (status) {
 case 'available': return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400';
 case 'recording': return 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
 case 'uploading': return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400';
 case 'error': return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400';
 case 'expired': return 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)] dark:bg-[rgba(31,41,55,0.2)] dark:text-[var(--text-muted,#9CA3AF)]';
 default: return 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)] dark:bg-[rgba(31,41,55,0.2)] dark:text-[var(--text-muted,#9CA3AF)]';
 }
}

function kategorieColor(kat) {
 switch (kat) {
 case 'pack': return 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
 case 'inbound': return 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400';
 case 'retoure': return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400';
 case 'referenz': return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400';
 default: return 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]';
 }
}

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════

export default function AufnahmenPageWrapper() {
 return <AufnahmenErrorBoundary><AufnahmenPageInner /></AufnahmenErrorBoundary>;
}

function AufnahmenPageInner() {
 const { t } = useLanguage();
 const [tab, setTab] = useTabParam('archiv');
 const [stats, setStats] = useState(null);
 const [loading, setLoading] = useState(true);

 const loadStats = useCallback(() => {
 fetch(`${API}/api/recordings/stats`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setStats(d))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { loadStats(); }, [loadStats]);

 const agentOnline = stats?.agent?.status === 'online';

 if (loading && !stats) {
 return (
 <div className="p-8 text-center text-[var(--text-tertiary,#6B7280)]">
 <div className="inline-block w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 );
 }

 return (
 <div className="p-6 md:p-8 space-y-6">
 <PageHeader title={t('recording.title') || 'Aufnahmen'} />

 {/* ── KPI Bar ── */}
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
 <KpiCard
 label={t('recording.stats.today') || 'Heute'}
 value={stats?.heute?.aufnahmen?.toLocaleString('de-AT') ?? '0'}
 icon={Video}
 />
 <KpiCard
 label={t('recording.stats.total') || 'Gesamt'}
 value={stats?.gesamt?.aufnahmen?.toLocaleString('de-AT') ?? '0'}
 icon={Camera}
 />
 <KpiCard
 label={t('recording.stats.storage') || 'Speicher'}
 value={formatStorageGB(stats?.gesamt?.speicher_gb)}
 icon={HardDrive}
 />
 <KpiCard
 label={t('recording.stats.agentStatus') || 'Agent'}
 value={
 <span className="flex items-center gap-2">
 <span className={`w-2.5 h-2.5 rounded-full inline-block ${agentOnline ? 'bg-emerald-500' : 'bg-red-500'}`} />
 {agentOnline ? 'Online' : 'Offline'}
 </span>
 }
 icon={Server}
 />
 </div>

 {/* ── Tab Bar ── */}
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)] inline-flex">
 {TABS.map(key => (
 <button
 key={key}
 onClick={() => setTab(key)}
 className={`px-4 py-2 text-[13px] font-medium rounded-[8px] transition-all ${
 tab === key
 ? 'bg-white text-[var(--text-primary,#1A1A2E)] shadow-sm font-semibold dark:bg-[var(--bg-card,#1A1D2B)]'
 : 'text-[var(--text-secondary,#4B5563)]'
 }`}
 >
 {t(`recording.tabs.${key}`) || key}
 </button>
 ))}
 </div>

 {/* ── Tab Content ── */}
 {tab === 'archiv' && <ArchivTab t={t} stats={stats} />}
 {tab === 'cameras' && <CamerasTab t={t} />}
 {tab === 'live' && <LiveTab t={t} stats={stats} onRefresh={loadStats} />}
 {tab === 'analyse' && <AnalyseTab t={t} stats={stats} />}
 {tab === 'onboarding' && <OnboardingTab t={t} stats={stats} />}
 {tab === 'settings' && <SettingsTab t={t} />}
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════
// TAB 1: ARCHIV
// ══════════════════════════════════════════════════════════════════

function ArchivTab({ t, stats }) {
 const [recordings, setRecordings] = useState([]);
 const [total, setTotal] = useState(0);
 const [page, setPage] = useState(1);
 const [searchQ, setSearchQ] = useState('');
 const [cameras, setCameras] = useState([]);
 const [filters, setFilters] = useState({ camera_id: '', kategorie: '', status: '', datum_von: '', datum_bis: '', nur_analysiert: false, max_score: '' });
 const [selected, setSelected] = useState(null);
 const [analyseDetail, setAnalyseDetail] = useState(null);
 const [loadingList, setLoadingList] = useState(true);
 const [checkedIds, setCheckedIds] = useState(new Set());
 const [deleteConfirm, setDeleteConfirm] = useState(null); // null | { id, auftrag_id } | 'bulk'
 const [deleting, setDeleting] = useState(false);
 const [toast, setToast] = useState(null);
 const debounceRef = useRef(null);
 const limit = 50;

 const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

 const toggleCheck = (id) => setCheckedIds(prev => {
 const next = new Set(prev);
 next.has(id) ? next.delete(id) : next.add(id);
 return next;
 });
 const toggleAll = () => {
 if (checkedIds.size === recordings.length) setCheckedIds(new Set());
 else setCheckedIds(new Set(recordings.map(r => r.id)));
 };

 const doDelete = async (ids) => {
 setDeleting(true);
 try {
 if (ids.length === 1) {
 const res = await fetch(`${API}/api/recordings/${ids[0]}`, { method: 'DELETE', credentials: 'include' });
 if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
 } else {
 const res = await fetch(`${API}/api/recordings/bulk-delete`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ids }),
 });
 if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Fehler');
 }
 showToast(t('recording.delete.success') || `${ids.length} Aufnahme(n) gelöscht`);
 setCheckedIds(new Set());
 loadData();
 } catch (err) {
 showToast(err.message, 'error');
 } finally {
 setDeleting(false);
 setDeleteConfirm(null);
 }
 };

 // Load cameras for filter dropdown
 useEffect(() => {
 fetch(`${API}/api/recordings/cameras`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setCameras(d?.data || []))
 .catch(() => {});
 }, []);

 const loadData = useCallback(() => {
 setLoadingList(true);
 const params = new URLSearchParams({ page, limit });
 if (searchQ) params.set('auftrag_id', searchQ);
 if (filters.camera_id) params.set('camera_id', filters.camera_id);
 if (filters.kategorie) params.set('kategorie', filters.kategorie);
 if (filters.status) params.set('status', filters.status);
 if (filters.datum_von) params.set('datum_von', filters.datum_von);
 if (filters.datum_bis) params.set('datum_bis', filters.datum_bis);
 if (filters.nur_analysiert) params.set('nur_analysiert', 'true');
 if (filters.max_score) params.set('max_score', filters.max_score);

 fetch(`${API}/api/recordings?${params}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { setRecordings(d?.data || []); setTotal(d?.total || 0); })
 .catch(() => {})
 .finally(() => setLoadingList(false));
 }, [page, searchQ, filters]);

 useEffect(() => { loadData(); }, [loadData]);

 const handleSearch = (val) => {
 setSearchQ(val);
 if (debounceRef.current) clearTimeout(debounceRef.current);
 debounceRef.current = setTimeout(() => setPage(1), 500);
 };

 const totalPages = Math.ceil(total / limit);

 return (
 <div className="space-y-4">
 {/* Search + Filters */}
 <div className="flex flex-wrap gap-3 items-end">
 <div className="relative flex-1 min-w-[260px]">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />
 <input
 type="text"
 value={searchQ}
 onChange={e => handleSearch(e.target.value)}
 placeholder={t('recording.searchPlaceholder') || 'z.B. WS-2026-048291'}
 className="w-full pl-10 pr-4 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] placeholder:text-[var(--text-muted,#9CA3AF)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/30"
 />
 {searchQ && (
 <button onClick={() => { setSearchQ(''); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]">
 <X className="w-4 h-4" />
 </button>
 )}
 </div>
 <input type="date" value={filters.datum_von} onChange={e => setFilters(f => ({ ...f, datum_von: e.target.value }))}
 className="px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
 <input type="date" value={filters.datum_bis} onChange={e => setFilters(f => ({ ...f, datum_bis: e.target.value }))}
 className="px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
 <select value={filters.camera_id} onChange={e => setFilters(f => ({ ...f, camera_id: e.target.value }))}
 className="px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
 <option value="">{t('recording.cameras.title') || 'Kamera'}</option>
 {cameras.map(c => <option key={c.camera_id} value={c.camera_id}>{c.name}</option>)}
 </select>
 <select value={filters.kategorie} onChange={e => setFilters(f => ({ ...f, kategorie: e.target.value }))}
 className="px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
 <option value="">{t('recording.kategorie.pack') ? 'Kategorie' : 'Kategorie'}</option>
 {KATEGORIEN.map(k => <option key={k} value={k}>{t(`recording.kategorie.${k}`) || k}</option>)}
 </select>
 <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
 className="px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
 <option value="">Status</option>
 {STATUS_LIST.map(s => <option key={s} value={s}>{t(`recording.status.${s}`) || s}</option>)}
 </select>
 <label className="flex items-center gap-1.5 text-sm text-[var(--text-secondary,#4B5563)] cursor-pointer whitespace-nowrap">
 <input type="checkbox" checked={filters.nur_analysiert} onChange={e => setFilters(f => ({ ...f, nur_analysiert: e.target.checked }))}
 className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)]" />
 {t('recording.analyse.onlyAnalysed') || 'Nur analysierte'}
 </label>
 <div className="relative" style={{ width: 100 }}>
 <input type="number" value={filters.max_score} onChange={e => setFilters(f => ({ ...f, max_score: e.target.value }))}
 placeholder={t('recording.analyse.scoreFilter') || 'Score <'}
 className="w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] placeholder:text-[var(--text-muted,#9CA3AF)]" />
 </div>
 </div>

 {/* Bulk-Actions Bar */}
 {checkedIds.size > 0 && (
 <div className="flex items-center gap-3 px-4 py-2.5 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] rounded-[10px]">
 <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
 {checkedIds.size} {t('recording.delete.selected') || 'ausgewählt'}
 </span>
 <button onClick={() => setDeleteConfirm('bulk')}
 className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors">
 <Trash2 className="w-3.5 h-3.5" />
 {t('recording.delete.bulkBtn') || 'Ausgewählte löschen'}
 </button>
 <button onClick={() => setCheckedIds(new Set())}
 className="text-sm text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)] transition-colors">
 {t('recording.delete.clearSelection') || 'Auswahl aufheben'}
 </button>
 </div>
 )}

 {/* Table */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] overflow-hidden">
 <div className="overflow-x-auto">
 <table className="w-full text-left">
 <thead>
 <tr className="border-b border-[var(--border-card,#E5E7EB)]">
 <th className="px-3 py-3 w-10">
 <input type="checkbox" checked={recordings.length > 0 && checkedIds.size === recordings.length}
 onChange={toggleAll} className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)] cursor-pointer" />
 </th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Auftrags-ID</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 140 }}>{t('recording.player.camera') || 'Kamera'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 100 }}>Kategorie</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 140 }}>Mitarbeiter</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 80 }}>{t('recording.player.duration') || 'Dauer'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 80 }}>{t('recording.player.filesize') || 'Größe'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 140 }}>Datum</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 100 }}>Status</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 60 }}>Score</th>
 <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 50 }}></th>
 </tr>
 </thead>
 <tbody>
 {loadingList && !recordings.length ? (
 <tr><td colSpan={99} className="px-4 py-12 text-center text-[var(--text-tertiary,#6B7280)]">
 <div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </td></tr>
 ) : recordings.length === 0 ? (
 <tr><td colSpan={99} className="px-4 py-12 text-center text-[var(--text-tertiary,#6B7280)]">
 <CameraOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
 <p className="text-sm">{t('recording.noRecordings') || 'Noch keine Aufnahmen vorhanden'}</p>
 </td></tr>
 ) : recordings.map(r => (
 <tr key={r.id} onClick={() => setSelected(r)} className="border-b border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer transition-colors" style={{ height: 52 }}>
 <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
 <input type="checkbox" checked={checkedIds.has(r.id)} onChange={() => toggleCheck(r.id)}
 className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)] cursor-pointer" />
 </td>
 <td className="px-4 py-2 text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{r.auftrag_id}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{r.camera_name || '—'}</td>
 <td className="px-4 py-2">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium ${kategorieColor(r.kategorie)}`}>
 {t(`recording.kategorie.${r.kategorie}`) || r.kategorie}
 </span>
 </td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{r.mitarbeiter_name || '—'}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] tabular-nums">{formatDuration(r.dauer_sekunden)}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] tabular-nums">{formatSize(r.dateigroesse_mb)}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{timeAgo(r.aufnahme_start)}</td>
 <td className="px-4 py-2">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium ${statusColor(r.status)}`}>
 {t(`recording.status.${r.status}`) || r.status}
 </span>
 </td>
 <td className="px-4 py-2">
 {r.gesamtbewertung != null ? (
 <button onClick={e => { e.stopPropagation(); setAnalyseDetail(r); }}
 className={`inline-flex px-2 py-0.5 rounded-lg text-[12px] font-bold tabular-nums hover:opacity-80 ${scoreColor(r.gesamtbewertung)}`}>
 {r.gesamtbewertung}
 </button>
 ) : null}
 </td>
 <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
 <button onClick={() => setDeleteConfirm({ id: r.id, auftrag_id: r.auftrag_id })}
 className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-red-500 hover:bg-red-50 transition-colors"
 title={t('recording.delete.title') || 'Aufnahme löschen'}>
 <Trash2 className="w-4 h-4" />
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 {total > limit && (
 <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-card,#E5E7EB)]">
 <span className="text-[12px] text-[var(--text-tertiary,#6B7280)]">
 {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} / {total.toLocaleString('de-AT')}
 </span>
 <div className="flex items-center gap-1">
 <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
 className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-30 text-[var(--text-secondary)]">
 <ChevronLeft className="w-4 h-4" />
 </button>
 <span className="text-[12px] text-[var(--text-secondary,#4B5563)] px-2">{page} / {totalPages}</span>
 <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
 className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-30 text-[var(--text-secondary)]">
 <ChevronRight className="w-4 h-4" />
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Video Player Modal */}
 {selected && <VideoPlayerModal recording={selected} onClose={() => setSelected(null)} t={t} />}
 {analyseDetail && <AnalyseDetailModal t={t} row={analyseDetail} onClose={() => setAnalyseDetail(null)} />}

 {/* Delete Confirm Dialog */}
 {deleteConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !deleting && setDeleteConfirm(null)}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
 <div className="px-6 py-5">
 <div className="flex items-center gap-3 mb-3">
 <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
 <Trash2 className="w-5 h-5 text-red-500" />
 </div>
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">
 {t('recording.delete.title') || 'Aufnahme löschen'}
 </h3>
 </div>
 <p className="text-sm text-[var(--text-secondary,#4B5563)] leading-relaxed">
 {deleteConfirm === 'bulk'
 ? (t('recording.delete.bulkConfirm') || `${checkedIds.size} Aufnahmen wirklich löschen? Die Videodateien werden unwiderruflich von der Storage Box entfernt.`)
 : (t('recording.delete.confirm') || `Aufnahme ${deleteConfirm.auftrag_id} wirklich löschen?`)}
 </p>
 </div>
 <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
 <button onClick={() => setDeleteConfirm(null)} disabled={deleting}
 className="px-4 py-2 text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card,#fff)] rounded-lg border border-[var(--border-card,#E5E7EB)] transition-colors disabled:opacity-50">
 {t('recording.delete.cancel') || 'Abbrechen'}
 </button>
 <button
 onClick={() => doDelete(deleteConfirm === 'bulk' ? [...checkedIds] : [deleteConfirm.id])}
 disabled={deleting}
 className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50">
 {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
 {deleting ? (t('recording.delete.deleting') || 'Löscht...') : (t('recording.delete.confirmBtn') || 'Löschen')}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Toast */}
 {toast && (
 <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-sm text-sm font-medium text-white ${toast.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
 {toast.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
 {toast.msg}
 </div>
 )}
 </div>
 );
}

// ── Video Player Modal ──

function VideoPlayerModal({ recording, onClose, t }) {
 const r = recording;
 const [shareUrl, setShareUrl] = useState(null);
 const [shareExpires, setShareExpires] = useState(null);
 const [shareLoading, setShareLoading] = useState(false);
 const [copied, setCopied] = useState(false);

 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && onClose();
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onClose]);

 const streamUrl = `${API}/api/recordings/${r.id}/stream`;

 const generateShare = async () => {
 setShareLoading(true);
 try {
 const resp = await fetch(`${API}/api/recordings/${r.id}/share`, { method: 'POST', credentials: 'include' });
 const data = await resp.json();
 if (data.ok) {
 setShareUrl(data.url);
 setShareExpires(data.expires_at);
 }
 } catch { /* ignore */ }
 setShareLoading(false);
 };

 const copyLink = () => {
 if (shareUrl) {
 navigator.clipboard.writeText(shareUrl);
 setCopied(true);
 setTimeout(() => setCopied(false), 2000);
 }
 };

 // Upload status steps
 const uploadSteps = [
 { key: 'recorded', icon: CheckCircle2, label: t('recording.upload.recorded') || 'Aufgenommen', time: r.aufnahme_start, done: !!r.aufnahme_start },
 { key: 'upload', icon: Upload, label: t('recording.upload.uploadStarted') || 'Upload gestartet', time: r.aufnahme_ende, done: !!r.aufnahme_ende && r.status !== 'recording' },
 { key: 'server', icon: Globe, label: t('recording.upload.onServer') || 'Auf Server',
 time: r.upload_abgeschlossen,
 done: r.status === 'available' && !!r.remote_path,
 pending: r.status === 'uploading',
 failed: r.status === 'error' },
 ];

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
 {/* Header */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <div>
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">{r.auftrag_id}</h3>
 <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5">{r.camera_name} — {formatDate(r.aufnahme_start)}</p>
 </div>
 <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]">
 <X className="w-5 h-5" />
 </button>
 </div>

 {/* Video + Info */}
 <div className="flex flex-col lg:flex-row">
 {/* Player */}
 <div className="flex-1 bg-black flex items-center justify-center min-h-[300px]">
 {r.status === 'available' && r.remote_path ? (
 <video controls className="w-full max-h-[60vh]" src={streamUrl}>
 Your browser does not support the video tag.
 </video>
 ) : (
 <div className="text-center text-[var(--text-muted,#9CA3AF)] p-8">
 <CameraOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
 <p className="text-sm">
 {r.status === 'error' ? r.fehler_details || 'Fehler bei der Aufnahme'
 : r.status === 'recording' ? t('recording.status.recording')
 : r.status === 'uploading' ? t('recording.status.uploading')
 : 'Video nicht verfügbar'}
 </p>
 </div>
 )}
 </div>

 {/* Sidebar Info */}
 <div className="w-full lg:w-[280px] p-5 space-y-4 border-t lg:border-t-0 lg:border-l border-[var(--border-card,#E5E7EB)] overflow-y-auto max-h-[60vh]">
 <InfoRow label={t('recording.player.camera') || 'Kamera'} value={r.camera_name || '—'} />
 <InfoRow label="Kategorie" value={t(`recording.kategorie.${r.kategorie}`) || r.kategorie} />
 <InfoRow label="Mitarbeiter" value={r.mitarbeiter_name || '—'} />
 <InfoRow label={t('recording.player.duration') || 'Dauer'} value={formatDuration(r.dauer_sekunden)} />
 <InfoRow label={t('recording.player.filesize') || 'Größe'} value={formatSize(r.dateigroesse_mb)} />
 <InfoRow label={t('recording.player.recorded') || 'Aufgenommen'} value={formatDate(r.aufnahme_start)} />
 <InfoRow label="Status">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium ${statusColor(r.status)}`}>
 {t(`recording.status.${r.status}`) || r.status}
 </span>
 </InfoRow>

 {/* Upload-Status Timeline */}
 <div className="pt-2 border-t border-[var(--border-card,#E5E7EB)]">
 <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-2">
 {t('recording.upload.title') || 'Upload-Status'}
 </p>
 <div className="space-y-2">
 {uploadSteps.map((step, i) => {
 const Icon = step.icon;
 const color = step.done ? 'text-emerald-500' : step.failed ? 'text-red-500' : step.pending ? 'text-amber-500' : 'text-[var(--text-muted,#9CA3AF)]';
 const label = step.failed ? (t('recording.upload.failed') || 'Fehlgeschlagen')
 : step.pending ? (t('recording.upload.pending') || 'Ausstehend')
 : step.done && step.time ? formatDate(step.time) : '—';
 return (
 <div key={step.key} className="flex items-center gap-2">
 <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
 <span className="text-xs text-[var(--text-secondary,#4B5563)] flex-1">{step.label}</span>
 <span className={`text-[11px] tabular-nums ${step.done ? 'text-emerald-600' : step.failed ? 'text-red-500' : step.pending ? 'text-amber-500' : 'text-[var(--text-muted,#9CA3AF)]'}`}>
 {step.done ? '✅' : step.failed ? '❌' : step.pending ? '⏳' : '—'}
 </span>
 </div>
 );
 })}
 </div>
 </div>

 {/* Actions */}
 <div className="space-y-2 pt-2 border-t border-[var(--border-card,#E5E7EB)]">
 {r.status === 'available' && r.remote_path && (
 <a href={streamUrl} download={r.dateiname || `${r.auftrag_id}.mp4`}
 className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors">
 <Download className="w-4 h-4" />
 {t('recording.player.download') || 'Herunterladen'}
 </a>
 )}

 {/* Share Link */}
 {r.status === 'available' && r.remote_path && !shareUrl && (
 <button onClick={generateShare} disabled={shareLoading}
 className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] text-sm font-medium hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors disabled:opacity-50">
 {shareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
 {shareLoading ? (t('recording.share.generating') || 'Wird erstellt...') : (t('recording.share.generate') || 'Share-Link erstellen')}
 </button>
 )}
 {shareUrl && (
 <div className="space-y-1.5">
 <div className="flex items-center gap-1">
 <input type="text" readOnly value={shareUrl}
 className="flex-1 text-[11px] px-2 py-1.5 rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] truncate" />
 <button onClick={copyLink}
 className={`p-1.5 rounded-lg transition-colors ${copied ? 'bg-emerald-50 text-emerald-600' : 'hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]'}`}
 title={t('recording.share.copy') || 'Link kopieren'}>
 {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
 </button>
 </div>
 {shareExpires && (
 <p className="text-[10px] text-[var(--text-muted,#9CA3AF)]">
 {t('recording.share.expires') || 'Gültig bis'}: {formatDate(shareExpires)}
 </p>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}

function InfoRow({ label, value, children }) {
 return (
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{label}</p>
 {children || <p className="text-sm text-[var(--text-primary,#1A1A2E)] mt-0.5">{value}</p>}
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════
// TAB 2: KAMERAS
// ══════════════════════════════════════════════════════════════════

function CamerasTab({ t }) {
 const [cameras, setCameras] = useState([]);
 const [loading, setLoading] = useState(true);
 const [modal, setModal] = useState(null); // null | 'add' | camera object
 const [testing, setTesting] = useState(null);
 const [toast, setToast] = useState(null);
 const [confirmDelete, setConfirmDelete] = useState(null);
 // Manual recording state
 const [activeRecs, setActiveRecs] = useState({}); // camera.id → { auftrag_id, startTime, duration_minutes }
 const [recTimers, setRecTimers] = useState({}); // camera.id → elapsed seconds
 const [startingRec, setStartingRec] = useState(null); // camera.id being started
 const [stoppingRec, setStoppingRec] = useState(null); // camera.id being stopped
 const [durationChoice, setDurationChoice] = useState({}); // camera.id → minutes or 0 (unlimited)
 // Snapshot state
 const [snapshotModal, setSnapshotModal] = useState(null); // camera object or null
 const [snapshotUrl, setSnapshotUrl] = useState(null);
 const [snapshotLoading, setSnapshotLoading] = useState(false);
 const [snapshotAutoRefresh, setSnapshotAutoRefresh] = useState(false);
 const snapshotIntervalRef = useRef(null);
 const timerIntervalRef = useRef(null);

 const load = useCallback(() => {
 setLoading(true);
 fetch(`${API}/api/recordings/cameras`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setCameras(d?.data || []))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { load(); }, [load]);

 // Timer tick for active recordings
 useEffect(() => {
 const keys = Object.keys(activeRecs);
 if (keys.length === 0) {
 if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
 return;
 }
 timerIntervalRef.current = setInterval(() => {
 setRecTimers(prev => {
 const next = { ...prev };
 for (const camId of Object.keys(activeRecs)) {
 const rec = activeRecs[camId];
 const elapsed = Math.round((Date.now() - rec.startTime) / 1000);
 next[camId] = elapsed;
 // Auto-stop if duration reached
 if (rec.duration_minutes && elapsed >= rec.duration_minutes * 60) {
 handleStopRecording(parseInt(camId), rec.auftrag_id);
 }
 }
 return next;
 });
 }, 1000);
 return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
 }, [activeRecs]);

 // Snapshot auto-refresh
 useEffect(() => {
 if (snapshotAutoRefresh && snapshotModal) {
 snapshotIntervalRef.current = setInterval(() => fetchSnapshot(snapshotModal.id), 5000);
 }
 return () => { if (snapshotIntervalRef.current) clearInterval(snapshotIntervalRef.current); };
 }, [snapshotAutoRefresh, snapshotModal]);

 // Cleanup snapshot URL on modal close
 useEffect(() => {
 if (!snapshotModal) {
 if (snapshotUrl) { URL.revokeObjectURL(snapshotUrl); setSnapshotUrl(null); }
 setSnapshotAutoRefresh(false);
 }
 }, [snapshotModal]);

 const showToast = (msg, type = 'success') => {
 setToast({ msg, type });
 setTimeout(() => setToast(null), 3000);
 };

 const handleTest = async (cam) => {
 setTesting(cam.id);
 try {
 const res = await fetch(`${API}/api/recordings/cameras/${cam.id}/test`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', credentials: 'include' });
 const d = await res.json();
 if (d.ok) {
 showToast(t('recording.cameras.testSuccess') || 'Verbindung erfolgreich', 'success');
 } else if (d.message && d.message.includes('Agent nicht erreichbar')) {
 showToast(t('recording.cameras.agentOffline') || 'Recording-Agent nicht erreichbar', 'error');
 } else {
 showToast(`${t('recording.cameras.testFailed') || 'Verbindung fehlgeschlagen'}: ${d.message || ''}`, 'error');
 }
 } catch { showToast(t('recording.cameras.agentOffline') || 'Recording-Agent nicht erreichbar', 'error'); }
 setTesting(null);
 };

 const handleStartRecording = async (cam) => {
 setStartingRec(cam.id);
 const dur = durationChoice[cam.id] || 0;
 try {
 const res = await fetch(`${API}/api/recordings/manual/start`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ camera_id: cam.id, duration_minutes: dur || null }),
 });
 const d = await res.json();
 if (d.ok) {
 setActiveRecs(prev => ({ ...prev, [cam.id]: { auftrag_id: d.auftrag_id, startTime: Date.now(), duration_minutes: dur || null } }));
 setRecTimers(prev => ({ ...prev, [cam.id]: 0 }));
 showToast(t('recording.manual.started') || 'Aufnahme gestartet');
 } else {
 showToast(d.error || (t('recording.snapshot.error') || 'Fehler'), 'error');
 }
 } catch { showToast(t('recording.cameras.agentOffline') || 'Recording-Agent nicht erreichbar', 'error'); }
 setStartingRec(null);
 };

 const handleStopRecording = async (camId, auftragId) => {
 const aid = auftragId || activeRecs[camId]?.auftrag_id;
 if (!aid) return;
 setStoppingRec(camId);
 try {
 const res = await fetch(`${API}/api/recordings/manual/stop`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ auftrag_id: aid }),
 });
 const d = await res.json();
 if (d.ok) {
 setActiveRecs(prev => { const n = { ...prev }; delete n[camId]; return n; });
 setRecTimers(prev => { const n = { ...prev }; delete n[camId]; return n; });
 showToast(t('recording.manual.stopped') || 'Aufnahme gestoppt');
 } else {
 showToast(d.error || 'Fehler', 'error');
 }
 } catch { showToast('Fehler', 'error'); }
 setStoppingRec(null);
 };

 const fetchSnapshot = async (camId) => {
 setSnapshotLoading(true);
 try {
 const res = await fetch(`${API}/api/recordings/cameras/${camId}/snapshot`, { credentials: 'include' });
 if (!res.ok) throw new Error('Snapshot failed');
 const blob = await res.blob();
 const url = URL.createObjectURL(blob);
 setSnapshotUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
 } catch {
 // keep last image if auto-refresh fails
 if (!snapshotUrl) setSnapshotUrl(null);
 }
 setSnapshotLoading(false);
 };

 const openSnapshot = (cam) => {
 setSnapshotModal(cam);
 fetchSnapshot(cam.id);
 };

 const doDelete = async () => {
 if (!confirmDelete) return;
 try {
 const res = await fetch(`${API}/api/recordings/cameras/${confirmDelete.id}`, { method: 'DELETE', credentials: 'include' });
 if (res.ok) { load(); showToast(t('common.deleted') || 'Gelöscht'); }
 else { const d = await res.json(); showToast(d.error || 'Fehler', 'error'); }
 } catch { showToast('Fehler', 'error'); }
 setConfirmDelete(null);
 };

 const handleSave = async (data) => {
 const isEdit = modal && modal !== 'add';
 const url = isEdit ? `${API}/api/recordings/cameras/${modal.id}` : `${API}/api/recordings/cameras`;
 try {
 const res = await fetch(url, {
 method: isEdit ? 'PUT' : 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(data),
 });
 if (res.ok) { load(); setModal(null); showToast(isEdit ? (t('recording.config.saved') || 'Gespeichert') : (t('recording.cameras.added') || 'Kamera hinzugefügt')); }
 else { const d = await res.json(); showToast(d.error || 'Fehler', 'error'); }
 } catch { showToast('Fehler', 'error'); }
 };

 const formatElapsed = (s) => { const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, '0')}`; };

 return (
 <div className="space-y-4">
 <div className="flex justify-end">
 <button onClick={() => setModal('add')}
 className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors">
 <Plus className="w-4 h-4" />
 {t('recording.cameras.add') || 'Kamera hinzufügen'}
 </button>
 </div>

 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] overflow-hidden">
 <div className="overflow-x-auto">
 <table className="w-full text-left">
 <thead>
 <tr className="border-b border-[var(--border-card,#E5E7EB)]">
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 40 }}></th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Kamera-ID</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Name</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">IP-Adresse</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Standort</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Typ</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Stream</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Aufnahmen</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 280 }}>Aktionen</th>
 </tr>
 </thead>
 <tbody>
 {loading && !cameras.length ? (
 <tr><td colSpan={9} className="px-4 py-12 text-center text-[var(--text-tertiary)]">
 <div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </td></tr>
 ) : cameras.length === 0 ? (
 <tr><td colSpan={9} className="px-4 py-12 text-center text-[var(--text-tertiary)]">
 <Camera className="w-8 h-8 mx-auto mb-2 opacity-40" />
 <p className="text-sm">Keine Kameras konfiguriert</p>
 </td></tr>
 ) : cameras.map(c => {
 const isRecording = !!activeRecs[c.id];
 return (
 <tr key={c.id} className="border-b border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors" style={{ height: 52 }}>
 <td className="px-4 py-2">
 <span className={`w-2.5 h-2.5 rounded-full inline-block ${c.aktiv ? 'bg-emerald-500' : 'bg-[var(--text-muted,#9CA3AF)]'}`} />
 </td>
 <td className="px-4 py-2 text-sm font-mono text-[var(--text-primary,#1A1A2E)]">{c.camera_id}</td>
 <td className="px-4 py-2 text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{c.name}</td>
 <td className="px-4 py-2 text-sm font-mono text-[var(--text-secondary,#4B5563)]">{c.ip_address}:{c.rtsp_port}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{c.standort || '—'}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{t(`recording.cameras.standortTyp.${c.standort_typ}`) || c.standort_typ}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] uppercase">{c.stream_type}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] tabular-nums">{c.total_aufnahmen || 0}</td>
 <td className="px-4 py-2">
 <div className="flex items-center gap-1">
 {/* Record / Stop */}
 {isRecording ? (
 <button onClick={() => handleStopRecording(c.id)} disabled={stoppingRec === c.id}
 className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-[12px] font-medium disabled:opacity-50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
 title={t('recording.manual.stop') || 'Aufnahme stoppen'}>
 {stoppingRec === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
 <span className="tabular-nums">{formatElapsed(recTimers[c.id] || 0)}</span>
 <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
 </button>
 ) : (
 <div className="flex items-center gap-0.5">
 <button onClick={() => handleStartRecording(c)} disabled={startingRec === c.id || !c.aktiv}
 className="flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-red-50 text-[var(--text-secondary)] hover:text-red-600 text-[12px] font-medium disabled:opacity-50 dark:hover:bg-red-900/20 dark:hover:text-red-400"
 title={t('recording.manual.start') || 'Aufnahme starten'}>
 {startingRec === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CircleDot className="w-3.5 h-3.5" />}
 </button>
 <select value={durationChoice[c.id] || 0} onChange={e => setDurationChoice(prev => ({ ...prev, [c.id]: parseInt(e.target.value) }))}
 className="text-[11px] py-1 px-1 rounded border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-tertiary,#6B7280)]"
 title={t('recording.manual.duration') || 'Dauer'}>
 <option value={0}>{t('recording.manual.unlimited') || '∞'}</option>
 <option value={1}>1 min</option>
 <option value={2}>2 min</option>
 <option value={5}>5 min</option>
 <option value={10}>10 min</option>
 </select>
 </div>
 )}
 {/* Snapshot */}
 <button onClick={() => openSnapshot(c)} disabled={!c.aktiv}
 className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] disabled:opacity-50" title={t('recording.snapshot.take') || 'Snapshot'}>
 <Image className="w-4 h-4" />
 </button>
 {/* Test */}
 <button onClick={() => handleTest(c)} disabled={testing === c.id}
 className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] disabled:opacity-50" title={t('recording.cameras.test')}>
 {testing === c.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
 </button>
 {/* Edit */}
 <button onClick={() => setModal(c)} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]" title={t('recording.cameras.edit')}>
 <Pencil className="w-4 h-4" />
 </button>
 {/* Delete */}
 <button onClick={() => setConfirmDelete(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title={t('recording.cameras.delete')}>
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </div>

 {/* Toast */}
 {toast && (
 <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-sm ${
 toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
 }`}>
 {toast.msg}
 </div>
 )}

 {/* Camera Modal */}
 {modal && <CameraModal camera={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={handleSave} t={t} />}

 {/* Snapshot Preview Modal */}
 {snapshotModal && (
 <SnapshotModal
 camera={snapshotModal}
 imageUrl={snapshotUrl}
 loading={snapshotLoading}
 autoRefresh={snapshotAutoRefresh}
 onToggleAutoRefresh={() => setSnapshotAutoRefresh(p => !p)}
 onRefresh={() => fetchSnapshot(snapshotModal.id)}
 onClose={() => setSnapshotModal(null)}
 t={t}
 isRecording={!!activeRecs[snapshotModal.id]}
 recElapsed={recTimers[snapshotModal.id] || 0}
 recStarting={startingRec === snapshotModal.id}
 recStopping={stoppingRec === snapshotModal.id}
 onStartRec={handleStartRecording}
 onStopRec={handleStopRecording}
 />
 )}

 {/* Confirm Delete Dialog */}
 {confirmDelete && (
 <ConfirmDialog
 title={t('recording.cameras.deleteTitle') || 'Kamera löschen'}
 message={`${t('recording.cameras.deleteConfirm') || 'Kamera wirklich löschen?'} (${confirmDelete.camera_id})`}
 confirmLabel={t('common.delete') || 'Löschen'}
 onConfirm={doDelete}
 onCancel={() => setConfirmDelete(null)}
 danger
 />
 )}
 </div>
 );
}

// ── Snapshot / Live-Stream Preview Modal ──

function SnapshotModal({ camera, imageUrl, loading, autoRefresh, onToggleAutoRefresh, onRefresh, onClose, t, autoStream = false,
 isRecording = false, recElapsed = 0, recStarting = false, recStopping = false, onStartRec, onStopRec }) {
 const [streamMode, setStreamMode] = useState(autoStream);
 const [streamStarting, setStreamStarting] = useState(false);
 const [streamActive, setStreamActive] = useState(false);
 const [streamError, setStreamError] = useState(null);
 const [showCloseWarn, setShowCloseWarn] = useState(false);
 const videoRef = useRef(null);
 const hlsRef = useRef(null);
 const streamStartedRef = useRef(false); // tracks if we sent a start request

 const fmtElapsed = (s) => { const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, '0')}`; };

 const handleClose = () => {
 if (isRecording) { setShowCloseWarn(true); return; }
 onClose();
 };

 useEffect(() => {
 const esc = (e) => { if (e.key === 'Escape') handleClose(); };
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [isRecording]); // eslint-disable-line react-hooks/exhaustive-deps

 // Cleanup on unmount / modal close
 useEffect(() => {
 return () => {
 if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
 if (streamStartedRef.current) {
 fetch(`${API}/api/recordings/cameras/${camera.id}/stream/stop`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', credentials: 'include',
 }).catch(() => {});
 }
 };
 }, []);

 // Auto-start stream when opened in stream mode
 useEffect(() => {
 if (autoStream && !streamActive && !streamStarting && !streamError) {
 startStream();
 }
 }, [autoStream]); // eslint-disable-line react-hooks/exhaustive-deps

 const startStream = async () => {
 setStreamError(null);
 setStreamStarting(true);
 try {
 const res = await fetch(`${API}/api/recordings/cameras/${camera.id}/stream/start`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', credentials: 'include',
 });
 if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Start failed');
 streamStartedRef.current = true;

 const Hls = (await import('hls.js')).default;
 const playlistUrl = `${API}/api/recordings/cameras/${camera.id}/stream/playlist.m3u8`;

 // Poll until playlist is ready (ffmpeg needs ~5s for first segments)
 let playlistReady = false;
 for (let attempt = 0; attempt < 15; attempt++) {
 await new Promise(r => setTimeout(r, 1000));
 try {
 const check = await fetch(playlistUrl, { credentials: 'include' });
 if (check.ok) { playlistReady = true; break; }
 } catch { /* retry */ }
 }
 if (!playlistReady) throw new Error('Playlist timeout — camera may be unreachable');

 if (Hls.isSupported()) {
 const hls = new Hls({
 liveDurationInfinity: true,
 enableWorker: false,
 manifestLoadingMaxRetry: 6,
 manifestLoadingRetryDelay: 1000,
 levelLoadingMaxRetry: 6,
 levelLoadingRetryDelay: 1000,
 fragLoadingMaxRetry: 6,
 fragLoadingRetryDelay: 1000,
 });
 hlsRef.current = hls;
 hls.loadSource(playlistUrl);
 hls.attachMedia(videoRef.current);
 hls.on(Hls.Events.MANIFEST_PARSED, () => {
 videoRef.current?.play().catch(() => {});
 setStreamActive(true);
 setStreamStarting(false);
 });
 hls.on(Hls.Events.ERROR, (event, data) => {
 if (data.fatal) {
 console.error('[HLS] Fatal error:', data);
 setStreamError(t('recording.stream.error') || 'Stream fehlgeschlagen');
 setStreamActive(false);
 setStreamStarting(false);
 hls.destroy();
 hlsRef.current = null;
 }
 });
 } else if (videoRef.current?.canPlayType('application/vnd.apple.mpegurl')) {
 // Native HLS (Safari)
 videoRef.current.src = playlistUrl;
 videoRef.current.addEventListener('loadedmetadata', () => {
 videoRef.current?.play().catch(() => {});
 setStreamActive(true);
 setStreamStarting(false);
 }, { once: true });
 } else {
 throw new Error('HLS not supported');
 }
 } catch (err) {
 console.error('[Stream] Start failed:', err);
 setStreamError(err.message || (t('recording.stream.error') || 'Stream fehlgeschlagen'));
 setStreamStarting(false);
 setStreamMode(false);
 }
 };

 const stopStream = () => {
 if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
 if (videoRef.current) { videoRef.current.pause(); videoRef.current.removeAttribute('src'); }
 setStreamActive(false);
 setStreamStarting(false);
 setStreamError(null);
 streamStartedRef.current = false;
 fetch(`${API}/api/recordings/cameras/${camera.id}/stream/stop`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', credentials: 'include',
 }).catch(() => {});
 };

 const toggleMode = (toStream) => {
 if (toStream === streamMode) return;
 if (toStream) {
 setStreamMode(true);
 startStream();
 } else {
 stopStream();
 setStreamMode(false);
 }
 };

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-[640px] p-5" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} onClick={e => e.stopPropagation()}>
 {/* Header */}
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-2">
 {streamMode ? <Video className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" /> : <Camera className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />}
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">
 {streamMode ? (t('recording.stream.mode') || 'Live-Stream') : (t('recording.snapshot.title') || 'Snapshot')} — {camera.name}
 </h3>
 {streamActive && (
 <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[11px] font-semibold dark:bg-red-900/20 dark:text-red-400">
 <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
 {t('recording.stream.active') || 'Live'}
 </span>
 )}
 </div>
 <div className="flex items-center gap-2">
 {isRecording && (
 <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[11px] font-semibold dark:bg-red-900/20 dark:text-red-400">
 <CircleDot className="w-3 h-3" />
 REC {fmtElapsed(recElapsed)}
 </span>
 )}
 <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]">
 <X className="w-5 h-5" />
 </button>
 </div>
 </div>

 {/* Video / Image area */}
 <div className="relative bg-black rounded-[10px] overflow-hidden mb-4" style={{ minHeight: 280 }}>
 {/* Snapshot Mode */}
 {!streamMode && (
 <>
 {loading && !imageUrl && (
 <div className="absolute inset-0 flex items-center justify-center">
 <Loader2 className="w-8 h-8 text-white animate-spin" />
 </div>
 )}
 {imageUrl ? (
 <img src={imageUrl} alt="Snapshot" className="w-full h-auto" />
 ) : !loading ? (
 <div className="flex flex-col items-center justify-center py-16 text-white/60">
 <CameraOff className="w-10 h-10 mb-2" />
 <p className="text-sm">{t('recording.snapshot.noImage') || 'Kein Bild verfügbar'}</p>
 </div>
 ) : null}
 {loading && imageUrl && (
 <div className="absolute top-2 right-2">
 <Loader2 className="w-4 h-4 text-white animate-spin" />
 </div>
 )}
 </>
 )}

 {/* Stream Mode */}
 {streamMode && (
 <>
 <video ref={videoRef} className="w-full h-auto" muted autoPlay playsInline
 style={{ display: streamActive ? 'block' : 'none' }} />
 {streamStarting && (
 <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
 <Loader2 className="w-8 h-8 animate-spin mb-2" />
 <p className="text-sm">{t('recording.stream.starting') || 'Stream wird gestartet...'}</p>
 </div>
 )}
 {streamError && (
 <div className="absolute inset-0 flex flex-col items-center justify-center text-red-400">
 <AlertTriangle className="w-8 h-8 mb-2" />
 <p className="text-sm">{streamError}</p>
 </div>
 )}
 </>
 )}
 </div>

 {/* Mode Toggle */}
 <div className="flex items-center justify-between">
 <div className="flex items-center">
 <div className="flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)]">
 <button onClick={() => toggleMode(false)}
 className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${!streamMode ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'}`}>
 <span className="flex items-center gap-1.5">
 <Image className="w-3.5 h-3.5" />
 {t('recording.stream.snapshot') || 'Snapshot'}
 </span>
 </button>
 <button onClick={() => toggleMode(true)} disabled={streamStarting}
 className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${streamMode ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'} disabled:opacity-50`}>
 <span className="flex items-center gap-1.5">
 <Video className="w-3.5 h-3.5" />
 {t('recording.stream.mode') || 'Live-Stream'}
 </span>
 </button>
 </div>
 </div>

 <div className="flex items-center gap-2">
 {!streamMode && (
 <>
 <label className="flex items-center gap-2 text-xs text-[var(--text-secondary,#4B5563)] cursor-pointer">
 <input type="checkbox" checked={autoRefresh} onChange={onToggleAutoRefresh}
 className="w-3.5 h-3.5 rounded border-[var(--border-card,#E5E7EB)]" />
 {t('recording.snapshot.autoRefresh') || 'Auto (5s)'}
 </label>
 <button onClick={onRefresh} disabled={loading}
 className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50 transition-colors">
 <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
 {t('recording.snapshot.take') || 'Snapshot'}
 </button>
 </>
 )}
 {streamMode && streamActive && (
 <>
 <button onClick={() => { if (videoRef.current) videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause(); }}
 className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 <Play className="w-4 h-4" />
 {t('recording.stream.playPause') || 'Play/Pause'}
 </button>
 {/* Record / Stop button */}
 {onStartRec && onStopRec && (
 isRecording ? (
 <button onClick={() => onStopRec(camera.id)} disabled={recStopping}
 className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50">
 {recStopping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
 <span className="tabular-nums">{fmtElapsed(recElapsed)}</span>
 <span>{t('recording.stream.stopRec') || 'Stop'}</span>
 </button>
 ) : (
 <button onClick={() => onStartRec(camera)} disabled={recStarting}
 className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50">
 {recStarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleDot className="w-4 h-4" />}
 {t('recording.stream.record') || 'Aufnahme'}
 </button>
 )
 )}
 </>
 )}
 <button onClick={handleClose}
 className="px-3 py-2 rounded-[10px] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 {t('recording.snapshot.close') || 'Schließen'}
 </button>
 </div>
 </div>
 </div>

 {/* Close warning during active recording */}
 {showCloseWarn && (
 <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setShowCloseWarn(false)}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-sm p-6" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} onClick={e => e.stopPropagation()}>
 <div className="flex items-center gap-3 mb-4">
 <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-50 dark:bg-amber-900/20">
 <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
 </div>
 <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
 {t('recording.stream.closeWarnTitle') || 'Aufnahme läuft'}
 </h3>
 </div>
 <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-6">
 {t('recording.stream.closeWarnMsg') || 'Eine Aufnahme läuft noch. Die Aufnahme wird im Hintergrund fortgesetzt.'}
 </p>
 <div className="flex justify-end gap-2">
 <button onClick={() => setShowCloseWarn(false)}
 className="px-4 py-2 rounded-[10px] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 {t('common.cancel') || 'Abbrechen'}
 </button>
 <button onClick={() => { setShowCloseWarn(false); onClose(); }}
 className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 {t('recording.stream.closeAnyway') || 'Trotzdem schließen'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ── Confirm Dialog ──

function ConfirmDialog({ title, message, confirmLabel, onConfirm, onCancel, danger }) {
 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && onCancel();
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onCancel]);

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-sm p-6" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} onClick={e => e.stopPropagation()}>
 <div className="flex items-center gap-3 mb-4">
 <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${danger ? 'bg-red-50 dark:bg-red-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
 <AlertTriangle className={`w-5 h-5 ${danger ? 'text-red-500' : 'text-amber-500'}`} />
 </div>
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">{title}</h3>
 </div>
 <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-6">{message}</p>
 <div className="flex justify-end gap-3">
 <button onClick={onCancel}
 className="px-4 py-2.5 rounded-[10px] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 {('Abbrechen')}
 </button>
 <button onClick={onConfirm}
 className={`px-5 py-2.5 rounded-[10px] text-sm font-medium text-white transition-colors ${danger ? 'bg-red-500 hover:bg-red-600' : 'bg-[#338edc] hover:bg-[#2b7bc4]'}`}>
 {confirmLabel}
 </button>
 </div>
 </div>
 </div>
 );
}

// ── Camera Add/Edit Modal ──

function CameraModal({ camera, onClose, onSave, t }) {
 const [form, setForm] = useState({
 camera_id: camera?.camera_id || '',
 name: camera?.name || '',
 ip_address: camera?.ip_address || '',
 rtsp_port: camera?.rtsp_port || 554,
 rtsp_user: camera?.rtsp_user || 'admin',
 rtsp_password: camera?.rtsp_password || '',
 stream_type: camera?.stream_type || 'sub',
 standort: camera?.standort || '',
 standort_typ: camera?.standort_typ || 'packtisch',
 aktiv: camera?.aktiv !== false,
 });
 const [showPw, setShowPw] = useState(false);
 const [errors, setErrors] = useState({});
 const isEdit = !!camera;

 const validate = () => {
 const e = {};
 if (!isEdit && !/^[a-z0-9-]+$/.test(form.camera_id)) e.camera_id = t('recording.cameras.validation.cameraIdFormat') || 'Nur Kleinbuchstaben, Zahlen und Bindestriche';
 if (!form.camera_id) e.camera_id = t('common.required') || 'Pflichtfeld';
 if (!form.name) e.name = t('common.required') || 'Pflichtfeld';
 if (!form.ip_address) e.ip_address = t('common.required') || 'Pflichtfeld';
 else if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(form.ip_address)) e.ip_address = t('recording.cameras.validation.ipFormat') || 'Ungültiges IPv4-Format';
 setErrors(e);
 return Object.keys(e).length === 0;
 };

 const handleSubmit = (e) => {
 e.preventDefault();
 if (validate()) onSave(form);
 };

 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && onClose();
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onClose]);

 const inputCls = (name) => `w-full px-3 py-2.5 text-sm rounded-[10px] border bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] placeholder:text-[var(--text-muted,#9CA3AF)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/30 ${errors[name] ? 'border-red-400' : 'border-[var(--border-card,#E5E7EB)]'}`;
 const selectCls = 'w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]';
 const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1.5';
 const errorCls = 'text-[11px] text-red-500 mt-1';

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-[640px] p-6" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }} onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between mb-5">
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">
 {isEdit ? (t('recording.cameras.edit') || 'Kamera bearbeiten') : (t('recording.cameras.add') || 'Kamera hinzufügen')}
 </h3>
 <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]">
 <X className="w-5 h-5" />
 </button>
 </div>
 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Row 1: Camera-ID + Name */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.cameraId') || 'Kamera-ID'}</label>
 <input type="text" value={form.camera_id} required readOnly={isEdit} placeholder="packtisch-07"
 onChange={e => setForm(f => ({ ...f, camera_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
 className={`${inputCls('camera_id')} ${isEdit ? 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)] cursor-not-allowed' : ''}`} />
 {errors.camera_id && <p className={errorCls}>{errors.camera_id}</p>}
 </div>
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.name') || 'Name'}</label>
 <input type="text" value={form.name} required placeholder="Packtisch 7 (links)"
 onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
 className={inputCls('name')} />
 {errors.name && <p className={errorCls}>{errors.name}</p>}
 </div>
 </div>

 {/* Row 2: IP + Port */}
 <div className="grid grid-cols-3 gap-4">
 <div className="col-span-2">
 <label className={labelCls}>{t('recording.cameras.fields.ipAddress') || 'IP-Adresse'}</label>
 <input type="text" value={form.ip_address} required placeholder="192.168.1.107"
 onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))}
 className={inputCls('ip_address')} />
 {errors.ip_address && <p className={errorCls}>{errors.ip_address}</p>}
 </div>
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.rtspPort') || 'RTSP-Port'}</label>
 <input type="number" value={form.rtsp_port}
 onChange={e => setForm(f => ({ ...f, rtsp_port: parseInt(e.target.value) || 554 }))}
 className={inputCls()} />
 </div>
 </div>

 {/* Row 3: RTSP User + Password */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.rtspUser') || 'RTSP-Benutzer'}</label>
 <input type="text" value={form.rtsp_user} placeholder="admin"
 onChange={e => setForm(f => ({ ...f, rtsp_user: e.target.value }))}
 className={inputCls()} />
 </div>
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.rtspPassword') || 'RTSP-Passwort'}</label>
 <div className="relative">
 <input type={showPw ? 'text' : 'password'} value={form.rtsp_password}
 onChange={e => setForm(f => ({ ...f, rtsp_password: e.target.value }))}
 className={inputCls()} />
 <button type="button" onClick={() => setShowPw(!showPw)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary)]">
 {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </button>
 </div>
 </div>
 </div>

 {/* Row 4: Stream + Standort + Typ */}
 <div className="grid grid-cols-3 gap-4">
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.streamType') || 'Stream-Typ'}</label>
 <select value={form.stream_type} onChange={e => setForm(f => ({ ...f, stream_type: e.target.value }))} className={selectCls}>
 {STREAM_TYPES.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
 </select>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">
 {t('recording.cameras.streamHint') || 'Sub-Stream (720p) empfohlen für optimale Speichernutzung'}
 </p>
 </div>
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.standort') || 'Standort'}</label>
 <input type="text" value={form.standort} placeholder="Halle A, Reihe 3"
 onChange={e => setForm(f => ({ ...f, standort: e.target.value }))}
 className={inputCls()} />
 </div>
 <div>
 <label className={labelCls}>{t('recording.cameras.fields.standortTyp') || 'Standort-Typ'}</label>
 <select value={form.standort_typ} onChange={e => setForm(f => ({ ...f, standort_typ: e.target.value }))} className={selectCls}>
 {STANDORT_TYPEN.map(s => <option key={s} value={s}>{t(`recording.cameras.standortTyp.${s}`) || s}</option>)}
 </select>
 </div>
 </div>

 {/* Aktiv Toggle */}
 <div className="flex items-center gap-3 pt-2">
 <label className="flex items-center gap-2 text-sm text-[var(--text-primary,#1A1A2E)] cursor-pointer">
 <input type="checkbox" checked={form.aktiv} onChange={e => setForm(f => ({ ...f, aktiv: e.target.checked }))}
 className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)]" />
 {t('recording.cameras.fields.aktiv') || 'Aktiv'}
 </label>
 </div>

 {/* Footer */}
 <div className="flex justify-end gap-3 pt-3 border-t border-[var(--border-card,#E5E7EB)]">
 <button type="button" onClick={onClose}
 className="px-4 py-2.5 rounded-[10px] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 {t('common.cancel') || 'Abbrechen'}
 </button>
 <button type="submit"
 className="px-5 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors">
 {t('common.save') || 'Speichern'}
 </button>
 </div>
 </form>
 </div>
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════
// TAB 3: LIVE
// ══════════════════════════════════════════════════════════════════

function LiveTab({ t, stats, onRefresh }) {
 const [activeRecordings, setActiveRecordings] = useState([]);
 const [loadingLive, setLoadingLive] = useState(true);
 const [stoppingManual, setStoppingManual] = useState(null);
 const [cameras, setCameras] = useState([]);
 const [streamPreview, setStreamPreview] = useState(null); // camera object for SnapshotModal (auto-stream)
 // Recording state for stream modal
 const [liveRecs, setLiveRecs] = useState({}); // camera.id → { auftrag_id, startTime }
 const [liveRecTimers, setLiveRecTimers] = useState({}); // camera.id → elapsed seconds
 const [liveStartingRec, setLiveStartingRec] = useState(null);
 const [liveStoppingRec, setLiveStoppingRec] = useState(null);

 // Timer tick for recording elapsed
 useEffect(() => {
 const keys = Object.keys(liveRecs);
 if (!keys.length) return;
 const iv = setInterval(() => {
 setLiveRecTimers(prev => {
 const next = { ...prev };
 for (const camId of keys) {
 const rec = liveRecs[camId];
 next[camId] = Math.floor((Date.now() - rec.startTime) / 1000);
 }
 return next;
 });
 }, 1000);
 return () => clearInterval(iv);
 }, [liveRecs]);

 const handleLiveStartRec = async (cam) => {
 setLiveStartingRec(cam.id);
 try {
 const res = await fetch(`${API}/api/recordings/manual/start`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ camera_id: cam.id, duration_minutes: null }),
 });
 const d = await res.json();
 if (d.ok) {
 setLiveRecs(prev => ({ ...prev, [cam.id]: { auftrag_id: d.auftrag_id, startTime: Date.now() } }));
 setLiveRecTimers(prev => ({ ...prev, [cam.id]: 0 }));
 }
 } catch { /* ignore */ }
 setLiveStartingRec(null);
 };

 const handleLiveStopRec = async (camId) => {
 const aid = liveRecs[camId]?.auftrag_id;
 if (!aid) return;
 setLiveStoppingRec(camId);
 try {
 const res = await fetch(`${API}/api/recordings/manual/stop`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ auftrag_id: aid }),
 });
 const d = await res.json();
 if (d.ok) {
 setLiveRecs(prev => { const n = { ...prev }; delete n[camId]; return n; });
 setLiveRecTimers(prev => { const n = { ...prev }; delete n[camId]; return n; });
 loadActive(); // refresh active recordings list
 }
 } catch { /* ignore */ }
 setLiveStoppingRec(null);
 };

 const loadActive = useCallback(() => {
 fetch(`${API}/api/recordings?status=recording&limit=50&sort_by=aufnahme_start&sort_dir=DESC`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setActiveRecordings(d?.data || []))
 .catch(() => {})
 .finally(() => setLoadingLive(false));
 }, []);

 // Load cameras once for stream-preview lookup
 useEffect(() => {
 fetch(`${API}/api/recordings/cameras`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setCameras(d?.data || []))
 .catch(() => {});
 }, []);

 const openStreamPreview = (recording) => {
 const cam = cameras.find(c => c.camera_id === recording.camera_id);
 if (cam) setStreamPreview(cam);
 };

 const handleManualStop = async (auftragId) => {
 setStoppingManual(auftragId);
 try {
 await fetch(`${API}/api/recordings/manual/stop`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ auftrag_id: auftragId }),
 });
 loadActive();
 } catch { /* ignore */ }
 setStoppingManual(null);
 };

 useEffect(() => {
 loadActive();
 const interval = setInterval(() => { loadActive(); onRefresh(); }, 5000);
 return () => clearInterval(interval);
 }, [loadActive, onRefresh]);

 const agent = stats?.agent || {};
 const heartbeatAge = agent.letzter_heartbeat ? (Date.now() - new Date(agent.letzter_heartbeat).getTime()) / 1000 : null;
 const agentOffline = !heartbeatAge || heartbeatAge > 120;

 return (
 <div className="space-y-4">
 {/* Agent offline warning */}
 {agentOffline && (
 <div className="flex items-center gap-3 px-4 py-3 rounded-[12px] bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-700/50 dark:text-amber-300">
 <WifiOff className="w-5 h-5 flex-shrink-0" />
 <p className="text-sm">
 Recording-Agent ist offline.{' '}
 {agent.letzter_heartbeat
 ? `Letzte Meldung vor ${Math.round((heartbeatAge || 0) / 60)} Minuten.`
 : 'Noch kein Heartbeat empfangen.'}
 </p>
 </div>
 )}

 {/* Agent Status Card */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-5">
 <Server className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.config.agent') || 'Recording-Agent'}</h3>
 <span className={`ml-auto flex items-center gap-1.5 text-sm font-medium ${agent.status === 'online' ? 'text-emerald-600' : 'text-red-500'}`}>
 <span className={`w-2.5 h-2.5 rounded-full ${agent.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'}`} />
 {agent.status === 'online' ? 'Online' : 'Offline'}
 </span>
 </div>
 <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
 <MetricBox label={t('recording.live.lastHeartbeat') || 'Letzter Heartbeat'} value={agent.letzter_heartbeat ? timeAgo(agent.letzter_heartbeat) : '—'} />
 <MetricBox label={t('recording.live.activeRecordings') || 'Aktive Aufnahmen'} value={agent.aktive_aufnahmen || 0} />
 <MetricBox label={t('recording.live.uploadQueue') || 'Upload-Queue'} value={agent.upload_queue || 0} />
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1">{t('recording.live.cpuUsage') || 'CPU'}</p>
 <div className="flex items-center gap-2">
 <div className="flex-1 h-2 bg-[var(--bg-inset,#F8F9FC)] rounded-full overflow-hidden">
 <div className="h-full bg-[#338edc] rounded-full transition-all" style={{ width: `${Math.min(agent.cpu_percent || 0, 100)}%` }} />
 </div>
 <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] tabular-nums">{Number(agent.cpu_percent || 0).toFixed(0)}%</span>
 </div>
 </div>
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1">{t('recording.live.diskFree') || 'Disk'}</p>
 <div className="flex items-center gap-2">
 <Database className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />
 <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] tabular-nums">{Number(agent.disk_free_gb || 0).toFixed(0)} GB</span>
 </div>
 </div>
 </div>
 </div>

 {/* Active Recordings */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-4">
 <Video className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.live.activeRecordings') || 'Aktive Aufnahmen'}</h3>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)]">Auto-Refresh 5s</span>
 </div>

 {loadingLive && !activeRecordings.length ? (
 <div className="py-8 text-center text-[var(--text-tertiary)]">
 <div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 ) : activeRecordings.length === 0 ? (
 <div className="py-8 text-center text-[var(--text-tertiary,#6B7280)]">
 <CameraOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
 <p className="text-sm">{t('recording.live.noActive') || 'Keine aktiven Aufnahmen'}</p>
 </div>
 ) : (
 <div className="space-y-2">
 {activeRecordings.map(r => {
 const isManual = r.auftrag_id && r.auftrag_id.startsWith('MANUELL-');
 return (
 <div key={r.id} className="flex items-center gap-4 px-4 py-3 rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)]">
 <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
 <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)] flex-1">{r.auftrag_id}</span>
 {isManual && (
 <span className="inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400">
 {t('recording.manual.badge') || 'Manuell'}
 </span>
 )}
 <span className="text-xs text-[var(--text-secondary,#4B5563)]">{r.camera_name || r.camera_id}</span>
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-medium ${kategorieColor(r.kategorie)}`}>
 {t(`recording.kategorie.${r.kategorie}`) || r.kategorie}
 </span>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)] tabular-nums">{timeAgo(r.aufnahme_start)}</span>
 <button onClick={() => openStreamPreview(r)}
 className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-[12px] font-medium dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-100/20"
 title={t('recording.stream.mode') || 'Live-Stream'}>
 <Eye className="w-3.5 h-3.5" />
 </button>
 {isManual && (
 <button onClick={() => handleManualStop(r.auftrag_id)}
 className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-[12px] font-medium dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
 title={t('recording.manual.stop') || 'Stoppen'}>
 <Square className="w-3.5 h-3.5" />
 </button>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>

 {/* Camera Overview Grid */}
 <CameraOverviewGrid cameras={cameras} activeRecordings={activeRecordings} t={t} onOpenStream={setStreamPreview} />

 {/* Stream Preview Modal (auto-starts in stream mode) */}
 {streamPreview && (
 <SnapshotModal
 camera={streamPreview}
 imageUrl={null}
 loading={false}
 autoRefresh={false}
 onToggleAutoRefresh={() => {}}
 onRefresh={() => {}}
 onClose={() => setStreamPreview(null)}
 t={t}
 autoStream
 isRecording={!!liveRecs[streamPreview.id]}
 recElapsed={liveRecTimers[streamPreview.id] || 0}
 recStarting={liveStartingRec === streamPreview.id}
 recStopping={liveStoppingRec === streamPreview.id}
 onStartRec={handleLiveStartRec}
 onStopRec={handleLiveStopRec}
 />
 )}
 </div>
 );
}

function MetricBox({ label, value }) {
 return (
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1">{label}</p>
 <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{value}</p>
 </div>
 );
}

// ── Camera Overview Grid (Live Tab) ──

function CameraOverviewGrid({ cameras, activeRecordings, t, onOpenStream }) {
 const [snapshots, setSnapshots] = useState({}); // camera.id → blob URL
 const [errors, setErrors] = useState({}); // camera.id → true
 const intervalsRef = useRef({});

 const activeCams = cameras.filter(c => c.aktiv);

 const fetchSnap = useCallback(async (cam) => {
 try {
 const res = await fetch(`${API}/api/recordings/cameras/${cam.id}/snapshot`, { credentials: 'include' });
 if (!res.ok) throw new Error();
 const blob = await res.blob();
 const url = URL.createObjectURL(blob);
 setSnapshots(prev => {
 if (prev[cam.id]) URL.revokeObjectURL(prev[cam.id]);
 return { ...prev, [cam.id]: url };
 });
 setErrors(prev => ({ ...prev, [cam.id]: false }));
 } catch {
 setErrors(prev => ({ ...prev, [cam.id]: true }));
 }
 }, []);

 // Initial fetch + 10s auto-refresh per camera
 useEffect(() => {
 activeCams.forEach(cam => {
 fetchSnap(cam);
 intervalsRef.current[cam.id] = setInterval(() => fetchSnap(cam), 10000);
 });
 return () => {
 Object.values(intervalsRef.current).forEach(clearInterval);
 intervalsRef.current = {};
 Object.values(snapshots).forEach(url => { try { URL.revokeObjectURL(url); } catch {} });
 };
 }, [activeCams.length]); // eslint-disable-line react-hooks/exhaustive-deps

 if (!activeCams.length) return null;

 const isRecording = (camId) => activeRecordings.some(r => r.camera_id === camId);

 return (
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-4">
 <Camera className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.live.cameraOverview') || 'Kamera-Übersicht'}</h3>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)]">Auto-Refresh 10s</span>
 </div>

 <div className="flex flex-wrap gap-3">
 {activeCams.map(cam => {
 const rec = isRecording(cam.camera_id);
 const snap = snapshots[cam.id];
 const err = errors[cam.id];
 return (
 <div key={cam.id} className="w-[280px] rounded-[10px] border border-[var(--border-card,#E5E7EB)] overflow-hidden bg-[var(--bg-card,#fff)]">
 {/* Header */}
 <div className="px-3 py-1.5 flex items-center gap-2 border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
 <span className={`w-2 h-2 rounded-full flex-shrink-0 ${!err ? 'bg-emerald-500' : 'bg-red-500'}`} />
 <span className="text-[12px] font-medium text-[var(--text-primary,#1A1A2E)] flex-1 truncate">{cam.name}</span>
 {rec && (
 <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
 <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
 REC
 </span>
 )}
 <button onClick={() => onOpenStream(cam)}
 className="p-1 rounded hover:bg-[var(--bg-card,#fff)] text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] transition-colors"
 title={t('recording.stream.mode') || 'Live-Stream'}>
 <Maximize2 className="w-3.5 h-3.5" />
 </button>
 </div>
 {/* Thumbnail — fixed height */}
 <div className="h-[158px] bg-black relative">
 {snap && !err ? (
 <img src={snap} alt={cam.name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-[var(--bg-inset,#F8F9FC)]">
 <CameraOff className="w-6 h-6 text-[var(--text-muted,#9CA3AF)] opacity-40" />
 </div>
 )}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════
// TAB 4: ANALYSE
// ══════════════════════════════════════════════════════════════════

function scoreColor(score) {
 if (score == null) return '';
 if (score >= 8) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400';
 if (score >= 5) return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400';
 return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400';
}

function AnalyseTab({ t, stats }) {
 const [analyseStats, setAnalyseStats] = useState(null);
 const [analyseLog, setAnalyseLog] = useState([]);
 const [logTotal, setLogTotal] = useState(0);
 const [logPage, setLogPage] = useState(1);
 const [referenz, setReferenz] = useState({ data: [], soll_map: {} });
 const [loading, setLoading] = useState(true);
 const [showAnalyseModal, setShowAnalyseModal] = useState(false);
 const [showSollModal, setShowSollModal] = useState(false);
 const [detailRow, setDetailRow] = useState(null);
 const [batchRunning, setBatchRunning] = useState(false);
 const [toast, setToast] = useState(null);

 const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

 const loadAll = useCallback(() => {
 setLoading(true);
 Promise.all([
 fetch(`${API}/api/recordings/analyse/stats`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 fetch(`${API}/api/recordings/analyse/log?page=${logPage}&limit=20`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 fetch(`${API}/api/recordings/referenz`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 ]).then(([s, l, ref]) => {
 if (s) setAnalyseStats(s);
 if (l) { setAnalyseLog(l.data || []); setLogTotal(l.total || 0); }
 if (ref) setReferenz(ref);
 }).catch(() => {}).finally(() => setLoading(false));
 }, [logPage]);

 useEffect(() => { loadAll(); }, [loadAll]);

 const handleBatch = async () => {
 setBatchRunning(true);
 try {
 const res = await fetch(`${API}/api/recordings/analyse/batch`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ modus: 'stichprobe', limit: 5 }),
 });
 const data = await res.json();
 if (data.gestartet > 0) {
 showToast(`${t('recording.analyse.batchDone') || 'Batch abgeschlossen'}: ${data.gestartet} Videos`);
 loadAll();
 } else {
 showToast(data.hinweis || 'Keine Videos', 'error');
 }
 } catch { showToast('Fehler', 'error'); }
 setBatchRunning(false);
 };

 const handleRemoveRef = async (id) => {
 const res = await fetch(`${API}/api/recordings/referenz/${id}`, { method: 'DELETE', credentials: 'include' });
 if (res.ok) { showToast(t('recording.analyse.sollRemoved') || 'Zuweisung entfernt'); loadAll(); }
 else showToast('Fehler', 'error');
 };

 const as = analyseStats;
 const logPages = Math.ceil(logTotal / 20);

 return (
 <div className="space-y-6">
 {/* KPI Cards */}
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
 <KpiCard label={t('recording.analyse.stats.analysed') || 'Analysiert'} value={as?.gesamt?.analysiert ?? 0} icon={BarChart3} color="bg-[#EBF4FD] text-[#338edc]" />
 <KpiCard label={t('recording.analyse.stats.avgScore') || 'Ø Bewertung'} value={as?.gesamt?.avg_score != null ? `${as.gesamt.avg_score}/10` : '—'} icon={Target} color="bg-[#ECFDF5] text-[#10B981]" />
 <KpiCard label={t('recording.analyse.stats.avgDeviation') || 'Ø Abweichung'} value={as?.gesamt?.avg_abweichung != null ? `${as.gesamt.avg_abweichung > 0 ? '+' : ''}${as.gesamt.avg_abweichung}%` : '—'} icon={TrendingUp} color="bg-[#ECFEFF] text-[#22D3EE]" />
 <KpiCard label={t('recording.analyse.stats.costs') || 'API-Kosten'} value={as?.woche?.kosten_eur != null ? `${as.woche.kosten_eur.toFixed(2)} €` : '—'} icon={Zap} color="bg-[#FFFBEB] text-[#F59E0B]" />
 </div>

 {/* Trend Chart + Abweichungen */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 {/* Trend-Chart: Ø Bewertung letzte 30 Tage */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)] mb-4">{t('recording.analyse.trend') || 'Bewertungstrend'}</h3>
 {(as?.trend_daily || []).length > 0 ? (
 <ResponsiveContainer width="100%" height={180}>
 <LineChart data={(as.trend_daily || []).map(d => ({ ...d, datum: new Date(d.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }), avg_score: parseFloat(d.avg_score), anzahl: parseInt(d.anzahl) }))}>
 <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
 <XAxis dataKey="datum" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
 <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
 <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)' }}
 formatter={(v, name) => [name === 'avg_score' ? `${v}/10` : v, name === 'avg_score' ? 'Ø Score' : 'Videos']} />
 <defs>
 <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
 <stop offset="5%" stopColor="#338edc" stopOpacity={0.15} />
 <stop offset="95%" stopColor="#338edc" stopOpacity={0} />
 </linearGradient>
 </defs>
 <Area type="monotone" dataKey="avg_score" fill="url(#scoreGrad)" stroke="none" />
 <Line type="monotone" dataKey="avg_score" stroke="#338edc" strokeWidth={2} dot={{ r: 3, fill: '#338edc' }} />
 </LineChart>
 </ResponsiveContainer>
 ) : (
 <p className="text-sm text-[var(--text-tertiary,#6B7280)] py-8 text-center">{t('recording.analyse.noAnalyses') || 'Noch keine Daten'}</p>
 )}
 </div>

 {/* Häufigste Abweichungen */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)] mb-4">{t('recording.analyse.topAbweichungen') || 'Häufigste Abweichungen'}</h3>
 {(as?.haeufigste_fehlende || []).length > 0 ? (
 <div className="space-y-3">
 {(as.haeufigste_fehlende || []).map((item, i) => {
 const maxCnt = (as.haeufigste_fehlende || [])[0]?.cnt || 1;
 const pct = Math.round((parseInt(item.cnt) / parseInt(maxCnt)) * 100);
 return (
 <div key={i} className="flex items-center gap-3">
 <span className="text-sm text-[var(--text-primary,#1A1A2E)] font-medium flex-1 truncate">{item.schritt}</span>
 <div className="w-24 h-2 rounded-full bg-[var(--bg-inset,#F8F9FC)] overflow-hidden">
 <div className="h-full rounded-full bg-red-400" style={{ width: `${pct}%` }} />
 </div>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)] tabular-nums w-8 text-right">{item.cnt}×</span>
 </div>
 );
 })}
 </div>
 ) : (
 <p className="text-sm text-[var(--text-tertiary,#6B7280)] py-8 text-center">{t('recording.analyse.noAnalyses') || 'Noch keine Daten'}</p>
 )}
 </div>
 </div>

 {/* SOLL-Videos + Actions */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <FileVideo className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.analyse.sollVideos') || 'SOLL-Referenzvideos'}</h3>
 </div>
 <div className="flex gap-2">
 <button onClick={() => setShowSollModal(true)}
 className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-xs font-medium bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 <Plus className="w-3.5 h-3.5" /> {t('recording.analyse.sollAssign') || 'SOLL-Video zuweisen'}
 </button>
 </div>
 </div>

 {['pack', 'inbound', 'retoure'].map(kat => {
 const sollId = referenz.soll_map?.[kat];
 const sollRec = sollId ? referenz.data.find(r => r.id === sollId) : null;

 return (
 <div key={kat} className="flex items-center gap-4 py-3 border-b border-[var(--border-card,#E5E7EB)] last:border-b-0">
 <span className={`inline-flex px-2.5 py-1 rounded-lg text-[11px] font-semibold uppercase ${kategorieColor(kat)}`}>
 {t(`recording.kategorie.${kat}`) || kat}
 </span>
 {sollRec ? (
 <div className="flex items-center gap-3 flex-1">
 <span className="text-sm text-[var(--text-primary,#1A1A2E)] font-medium">{sollRec.auftrag_id}</span>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{formatDuration(sollRec.dauer_sekunden)}</span>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{sollRec.camera_name || '—'}</span>
 <button onClick={() => handleRemoveRef(sollRec.id)}
 className="ml-auto p-1.5 rounded-lg hover:bg-red-50 text-red-500 text-xs">
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </div>
 ) : (
 <span className="text-xs text-[var(--text-muted,#9CA3AF)] italic">{t('recording.analyse.sollNone') || 'Kein SOLL-Video'}</span>
 )}
 </div>
 );
 })}
 </div>

 {/* Manual Analyse + Batch */}
 <div className="flex gap-3">
 <button onClick={() => setShowAnalyseModal(true)}
 className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors">
 <BarChart3 className="w-4 h-4" /> {t('recording.analyse.startAnalysis') || 'Video analysieren'}
 </button>
 <button onClick={handleBatch} disabled={batchRunning}
 className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors disabled:opacity-50">
 {batchRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
 {batchRunning ? (t('recording.analyse.batchRunning') || 'Batch läuft...') : (t('recording.analyse.batchStart') || 'Batch-Analyse')}
 </button>
 </div>

 {/* Letzte Analysen Tabelle */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] overflow-hidden">
 <div className="px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.analyse.lastAnalyses') || 'Letzte Analysen'}</h3>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full text-left">
 <thead>
 <tr className="border-b border-[var(--border-card,#E5E7EB)]">
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Auftrags-ID</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 100 }}>{t('recording.player.camera') || 'Kamera'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 100 }}>Mitarbeiter</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 80 }}>{t('recording.analyse.score') || 'Score'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 80 }}>{t('recording.analyse.stepsCorrect') || 'Schritte'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 80 }}>{t('recording.analyse.timeDeviation') || 'Zeit-Abw.'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{t('recording.analyse.mainIssue') || 'Hauptproblem'}</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]" style={{ width: 130 }}>Datum</th>
 </tr>
 </thead>
 <tbody>
 {loading && !analyseLog.length ? (
 <tr><td colSpan={8} className="px-4 py-12 text-center text-[var(--text-tertiary)]">
 <div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </td></tr>
 ) : analyseLog.length === 0 ? (
 <tr><td colSpan={8} className="px-4 py-12 text-center text-[var(--text-tertiary)]">
 <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
 <p className="text-sm">{t('recording.analyse.noAnalyses') || 'Noch keine Analysen'}</p>
 </td></tr>
 ) : analyseLog.map(row => (
 <tr key={row.id} onClick={() => setDetailRow(row)} className="border-b border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer transition-colors" style={{ height: 52 }}>
 <td className="px-4 py-2 text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{row.auftrag_id}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{row.camera_name || '—'}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{row.mitarbeiter_name || '—'}</td>
 <td className="px-4 py-2">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[12px] font-bold tabular-nums ${scoreColor(row.gesamtbewertung)}`}>
 {row.gesamtbewertung != null ? row.gesamtbewertung : '—'}
 </span>
 </td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] tabular-nums">
 {row.schritte_korrekt != null ? `${row.schritte_korrekt}/${row.schritte_gesamt}` : '—'}
 </td>
 <td className="px-4 py-2 text-sm tabular-nums">
 {row.zeit_abweichung_pct != null ? (
 <span className={row.zeit_abweichung_pct > 20 ? 'text-red-600' : row.zeit_abweichung_pct > 0 ? 'text-amber-600' : 'text-emerald-600'}>
 {row.zeit_abweichung_pct > 0 ? '+' : ''}{row.zeit_abweichung_pct}%
 </span>
 ) : '—'}
 </td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] truncate max-w-[200px]">{row.hauptproblem || '—'}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{timeAgo(row.analyse_timestamp)}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 {logTotal > 20 && (
 <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-card,#E5E7EB)]">
 <span className="text-[12px] text-[var(--text-tertiary)]">{((logPage - 1) * 20) + 1}–{Math.min(logPage * 20, logTotal)} / {logTotal}</span>
 <div className="flex items-center gap-1">
 <button onClick={() => setLogPage(p => Math.max(1, p - 1))} disabled={logPage <= 1} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset)] disabled:opacity-30 text-[var(--text-secondary)]"><ChevronLeft className="w-4 h-4" /></button>
 <span className="text-[12px] text-[var(--text-secondary)] px-2">{logPage} / {logPages}</span>
 <button onClick={() => setLogPage(p => Math.min(logPages, p + 1))} disabled={logPage >= logPages} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset)] disabled:opacity-30 text-[var(--text-secondary)]"><ChevronRight className="w-4 h-4" /></button>
 </div>
 </div>
 )}
 </div>

 {/* Modals */}
 {showAnalyseModal && <AnalyseStartModal t={t} onClose={() => setShowAnalyseModal(false)} onDone={() => { setShowAnalyseModal(false); loadAll(); }} showToast={showToast} />}
 {showSollModal && <SollZuweisenModal t={t} onClose={() => setShowSollModal(false)} onDone={() => { setShowSollModal(false); loadAll(); }} showToast={showToast} />}
 {detailRow && <AnalyseDetailModal t={t} row={detailRow} onClose={() => setDetailRow(null)} />}

 {toast && (
 <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-sm ${
 toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
 }`}>{toast.msg}</div>
 )}
 </div>
 );
}

// ── Analyse Start Modal ──

function AnalyseStartModal({ t, onClose, onDone, showToast }) {
 const [searchQ, setSearchQ] = useState('');
 const [results, setResults] = useState([]);
 const [selectedRec, setSelectedRec] = useState(null);
 const [analysing, setAnalysing] = useState(false);
 const [result, setResult] = useState(null);
 const [elapsed, setElapsed] = useState(0);
 const [statusText, setStatusText] = useState('');
 const debounceRef = useRef(null);
 const abortRef = useRef(null);

 const statusSteps = useMemo(() => [
 { after: 0, text: t('recording.analyseStep1') || 'Video wird hochgeladen...' },
 { after: 5, text: t('recording.analyseStep2') || 'AI analysiert das Video...' },
 { after: 15, text: t('recording.analyseStep3') || 'Prozessschritte werden erkannt...' },
 { after: 30, text: t('recording.analyseStep4') || 'Bewertung wird erstellt...' },
 { after: 45, text: t('recording.analyseStep5') || 'Analyse dauert länger als üblich...' },
 { after: 90, text: t('recording.analyseStep6') || 'Bitte Geduld — größere Videos brauchen mehr Zeit' },
 ], [t]);

 // Elapsed timer + status text
 useEffect(() => {
 if (!analysing) return;
 const start = Date.now();
 const iv = setInterval(() => {
 const sec = Math.floor((Date.now() - start) / 1000);
 setElapsed(sec);
 const msg = [...statusSteps].reverse().find(s => sec >= s.after);
 if (msg) setStatusText(msg.text);
 }, 1000);
 return () => clearInterval(iv);
 }, [analysing, statusSteps]);

 const doSearch = (q) => {
 setSearchQ(q);
 if (debounceRef.current) clearTimeout(debounceRef.current);
 debounceRef.current = setTimeout(() => {
 if (q.length < 2) { setResults([]); return; }
 fetch(`${API}/api/recordings/search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setResults((d?.data || []).filter(r => r.status === 'available')))
 .catch(() => {});
 }, 400);
 };

 const startAnalyse = async () => {
 if (!selectedRec) return;
 setAnalysing(true);
 setElapsed(0);
 setStatusText(statusSteps[0].text);
 const controller = new AbortController();
 abortRef.current = controller;
 const timeoutId = setTimeout(() => controller.abort(), 180000);
 try {
 const res = await fetch(`${API}/api/recordings/${selectedRec.id}/analyse`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ modus: 'vollanalyse' }),
 signal: controller.signal,
 });
 const data = await res.json();
 if (data.success) {
 setResult(data.ergebnis);
 if (data.partial) {
 showToast(t('recording.analyse.partialResult') || 'Analyse teilweise — Gemini konnte kein vollständiges Ergebnis liefern.', 'warning');
 } else {
 showToast(t('recording.analyse.analysisDone') || 'Analyse abgeschlossen');
 }
 } else {
 showToast(data.error || 'Fehler', 'error');
 }
 } catch (err) {
 if (err.name === 'AbortError') {
 showToast(t('recording.analyseTimeout') || 'Analyse abgebrochen (Zeitüberschreitung).', 'error');
 } else {
 showToast(err.message, 'error');
 }
 } finally {
 clearTimeout(timeoutId);
 abortRef.current = null;
 setAnalysing(false);
 }
 };

 const handleCancel = () => {
 if (abortRef.current) abortRef.current.abort();
 };

 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && !analysing && (result ? onDone() : onClose());
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onClose, onDone, result, analysing]);

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={!analysing ? (result ? onDone : onClose) : undefined}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between mb-5">
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.analyse.startAnalysis') || 'Video analysieren'}</h3>
 {!analysing && <button onClick={result ? onDone : onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]"><X className="w-5 h-5" /></button>}
 </div>

 {!result && !analysing && (
 <>
 {/* Search */}
 <div className="relative mb-4">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
 <input type="text" value={searchQ} onChange={e => doSearch(e.target.value)} placeholder={t('recording.searchPlaceholder') || 'Auftrags-ID suchen...'}
 className="w-full pl-10 pr-4 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 {results.length > 0 && (
 <div className="border border-[var(--border-card,#E5E7EB)] rounded-[10px] max-h-48 overflow-y-auto mb-4">
 {results.map(r => (
 <button key={r.id} onClick={() => { setSelectedRec(r); setResults([]); setSearchQ(r.auftrag_id); }}
 className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--bg-inset)] border-b border-[var(--border-card)] last:border-b-0 ${selectedRec?.id === r.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
 <span className="text-sm font-medium text-[var(--text-primary)]">{r.auftrag_id}</span>
 <span className="text-xs text-[var(--text-tertiary)]">{r.camera_name || ''}</span>
 <span className="text-xs text-[var(--text-tertiary)] ml-auto">{formatDuration(r.dauer_sekunden)}</span>
 </button>
 ))}
 </div>
 )}
 {selectedRec && (
 <div className="flex items-center gap-3 p-3 rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card)] mb-4">
 <Video className="w-5 h-5 text-[#338edc]" />
 <div>
 <p className="text-sm font-medium text-[var(--text-primary)]">{selectedRec.auftrag_id}</p>
 <p className="text-xs text-[var(--text-tertiary)]">{selectedRec.camera_name} — {formatDuration(selectedRec.dauer_sekunden)}</p>
 </div>
 </div>
 )}
 <button onClick={startAnalyse} disabled={!selectedRec}
 className="w-full px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
 {t('recording.analyse.startAnalysis') || 'Analyse starten'}
 </button>
 </>
 )}

 {analysing && (
 <div className="flex flex-col items-center gap-3 py-8">
 <div className="w-10 h-10 border-[3px] border-[var(--border-card,#E5E7EB)] border-t-[#338edc] rounded-full animate-spin" />
 <p className="text-sm font-medium text-[var(--text-primary)]">{statusText}</p>
 <p className="text-xs text-[var(--text-muted,#9CA3AF)]">{t('recording.analyseHinweis') || 'Dies kann je nach Videolänge 30–60 Sekunden dauern'}</p>
 <p className="text-xs text-[var(--text-tertiary)] font-mono tabular-nums">{elapsed}s</p>
 {elapsed > 120 && (
 <div className="flex items-center gap-2 mt-2 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
 <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
 <span className="text-xs text-amber-700 dark:text-amber-400">{t('recording.analyseLange') || 'Analyse dauert ungewöhnlich lange'}</span>
 <button onClick={handleCancel} className="text-xs text-red-500 hover:text-red-700 underline ml-2">{t('recording.abbrechen') || 'Abbrechen'}</button>
 </div>
 )}
 </div>
 )}

 {result && (
 <div>
 <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[var(--border-card,#E5E7EB)]">
 <CheckCircle2 className="w-5 h-5 text-emerald-500" />
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('recording.analyseErfolgreich') || 'Analyse abgeschlossen'}</span>
 <span className="text-xs text-[var(--text-tertiary)] ml-auto">{elapsed}s</span>
 </div>
 <AnalyseErgebnis erg={result} t={t} />
 </div>
 )}
 </div>
 </div>
 );
}

// ── Analyse Detail Modal (from table click) ──

function AnalyseDetailModal({ t, row, onClose }) {
 const [detail, setDetail] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetch(`${API}/api/recordings/${row.id}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setDetail(d))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, [row.id]);

 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && onClose();
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onClose]);

 const erg = detail?.analyse_ergebnis;

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-[95vw] max-w-7xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card)]">
 <div>
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">{row.auftrag_id} — {t('recording.analyse.title') || 'Analyse'}</h3>
 <p className="text-xs text-[var(--text-tertiary)] mt-0.5">{row.camera_name} — {formatDate(row.analyse_timestamp)}</p>
 </div>
 <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]"><X className="w-5 h-5" /></button>
 </div>
 <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(85vh - 80px)' }}>
 {loading ? (
 <div className="py-8 text-center"><div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" /></div>
 ) : erg ? (
 <div className="flex flex-col lg:flex-row gap-6">
 {/* Video player */}
 <div className="flex-1">
 {detail?.remote_path && (
 <video controls className="w-full rounded-lg bg-black" src={`${API}/api/recordings/${row.id}/stream`} />
 )}
 </div>
 <div className="flex-1">
 <AnalyseErgebnis erg={erg} t={t} />
 </div>
 </div>
 ) : (
 <p className="text-sm text-[var(--text-tertiary)]">Keine Analyse-Daten</p>
 )}
 </div>
 </div>
 </div>
 );
}

// ── Analyse Ergebnis Display (reusable) ──

function AnalyseErgebnis({ erg, t }) {
 if (!erg) return null;

 // parse_error: Gemini konnte kein JSON liefern
 if (erg.parse_error) {
 return (
 <div className="space-y-4">
 <div className="px-4 py-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
 <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">{t('recording.analyse.parseError') || 'Analyse unvollständig'}</p>
 <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">{t('recording.analyse.parseErrorHint') || 'Gemini konnte kein strukturiertes Ergebnis liefern. Bitte erneut versuchen.'}</p>
 </div>
 {erg.raw_text && (
 <div className="px-4 py-3 rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card)]">
 <p className="text-xs font-medium text-[var(--text-tertiary)] mb-1">Gemini Response:</p>
 <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{erg.raw_text}</p>
 </div>
 )}
 </div>
 );
 }

 return (
 <div className="space-y-4">
 {/* Score + Summary */}
 <div className="flex items-center gap-4">
 <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold ${scoreColor(erg.gesamtbewertung)}`}>
 {erg.gesamtbewertung ?? '—'}
 </div>
 <div className="flex-1">
 <p className="text-sm font-medium text-[var(--text-primary)]">{erg.zusammenfassung || ''}</p>
 {erg.hauptproblem && (
 <p className="text-xs text-red-600 mt-1">{t('recording.analyse.mainIssue') || 'Hauptproblem'}: {erg.hauptproblem}</p>
 )}
 {erg.top_tipp && (
 <p className="text-xs text-emerald-600 mt-1">{t('recording.analyse.topTip') || 'Tipp'}: {erg.top_tipp}</p>
 )}
 </div>
 </div>

 {/* Schritte */}
 {(erg.schritte || []).length > 0 && (
 <div className="border border-[var(--border-card,#E5E7EB)] rounded-[10px] overflow-hidden">
 <table className="w-full text-left">
 <thead>
 <tr className="bg-[var(--bg-inset,#F8F9FC)]">
 <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]">#</th>
 <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]">{t('recording.analyse.detail.schritt') || 'Schritt'}</th>
 <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]" style={{ width: 40 }}></th>
 <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]" style={{ width: 60 }}>{t('recording.analyse.detail.dauerIst') || 'IST'}</th>
 <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]" style={{ width: 60 }}>{t('recording.analyse.detail.dauerSoll') || 'SOLL'}</th>
 <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]">{t('recording.analyse.detail.tipp') || 'Tipp'}</th>
 </tr>
 </thead>
 <tbody>
 {erg.schritte.map((s, i) => (
 <tr key={i} className="border-t border-[var(--border-card)]">
 <td className="px-3 py-2 text-xs text-[var(--text-tertiary)] tabular-nums">{s.nr || i + 1}</td>
 <td className="px-3 py-2 text-xs font-medium text-[var(--text-primary)]">{s.name}</td>
 <td className="px-3 py-2">
 {s.erkannt === false ? <XCircle className="w-4 h-4 text-red-500" />
 : s.reihenfolge_korrekt === false ? <AlertTriangle className="w-4 h-4 text-amber-500" />
 : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
 </td>
 <td className="px-3 py-2 text-xs tabular-nums text-[var(--text-secondary)]">{s.dauer_ist_sek != null ? `${s.dauer_ist_sek}s` : '—'}</td>
 <td className="px-3 py-2 text-xs tabular-nums text-[var(--text-tertiary)]">{s.dauer_soll_sek != null ? `${s.dauer_soll_sek}s` : '—'}</td>
 <td className="px-3 py-2 text-xs text-[var(--text-secondary)]">{s.tipp || s.abweichung || ''}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}

 {/* Fehlende Schritte */}
 {(erg.fehlende_schritte || []).length > 0 && (
 <div className="px-3 py-2 rounded-[10px] bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50">
 <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">{t('recording.analyse.missingSteps') || 'Fehlende Schritte'}:</p>
 <p className="text-xs text-red-600 dark:text-red-300">{erg.fehlende_schritte.join(', ')}</p>
 </div>
 )}

 {/* Gesamt-Zeiten */}
 {erg.gesamt_dauer_ist_sek && (
 <div className="flex gap-4 text-xs text-[var(--text-tertiary)]">
 <span>Gesamt IST: <strong className="text-[var(--text-primary)]">{formatDuration(erg.gesamt_dauer_ist_sek)}</strong></span>
 {erg.gesamt_dauer_soll_sek && <span>SOLL: <strong className="text-[var(--text-primary)]">{formatDuration(erg.gesamt_dauer_soll_sek)}</strong></span>}
 </div>
 )}
 </div>
 );
}

// ── SOLL-Video zuweisen Modal ──

function SollZuweisenModal({ t, onClose, onDone, showToast }) {
 const [searchQ, setSearchQ] = useState('');
 const [results, setResults] = useState([]);
 const [selectedRec, setSelectedRec] = useState(null);
 const [kategorie, setKategorie] = useState('pack');
 const [saving, setSaving] = useState(false);
 const debounceRef = useRef(null);

 const doSearch = (q) => {
 setSearchQ(q);
 if (debounceRef.current) clearTimeout(debounceRef.current);
 debounceRef.current = setTimeout(() => {
 if (q.length < 2) { setResults([]); return; }
 fetch(`${API}/api/recordings/search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setResults((d?.data || []).filter(r => r.status === 'available')))
 .catch(() => {});
 }, 400);
 };

 const handleSave = async () => {
 if (!selectedRec) return;
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/recordings/referenz`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ recording_id: selectedRec.id, kategorie }),
 });
 if (res.ok) { showToast(t('recording.analyse.sollAssigned') || 'SOLL-Video zugewiesen'); onDone(); }
 else { const d = await res.json(); showToast(d.error || 'Fehler', 'error'); }
 } catch { showToast('Fehler', 'error'); }
 setSaving(false);
 };

 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && onClose();
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onClose]);

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between mb-5">
 <h3 className="text-base font-bold text-[var(--text-primary)]">{t('recording.analyse.sollAssign') || 'SOLL-Video zuweisen'}</h3>
 <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]"><X className="w-5 h-5" /></button>
 </div>

 <div className="space-y-4">
 <div>
 <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">Kategorie</label>
 <select value={kategorie} onChange={e => setKategorie(e.target.value)}
 className="w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)]">
 <option value="pack">{t('recording.kategorie.pack') || 'Pack'}</option>
 <option value="inbound">{t('recording.kategorie.inbound') || 'Inbound'}</option>
 <option value="retoure">{t('recording.kategorie.retoure') || 'Retoure'}</option>
 </select>
 </div>

 <div>
 <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">Video suchen</label>
 <div className="relative">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
 <input type="text" value={searchQ} onChange={e => doSearch(e.target.value)} placeholder="Auftrags-ID..."
 className="w-full pl-10 pr-4 py-2.5 text-sm rounded-[10px] border border-[var(--border-card)] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>

 {results.length > 0 && (
 <div className="border border-[var(--border-card)] rounded-[10px] max-h-40 overflow-y-auto">
 {results.map(r => (
 <button key={r.id} onClick={() => { setSelectedRec(r); setResults([]); setSearchQ(r.auftrag_id); }}
 className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--bg-inset)] border-b border-[var(--border-card)] last:border-b-0">
 <span className="text-sm font-medium text-[var(--text-primary)]">{r.auftrag_id}</span>
 <span className="text-xs text-[var(--text-tertiary)] ml-auto">{formatDuration(r.dauer_sekunden)}</span>
 </button>
 ))}
 </div>
 )}

 {selectedRec && (
 <div className="flex items-center gap-3 p-3 rounded-[10px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200">
 <Video className="w-5 h-5 text-[#338edc]" />
 <p className="text-sm font-medium text-[var(--text-primary)]">{selectedRec.auftrag_id}</p>
 </div>
 )}

 <button onClick={handleSave} disabled={!selectedRec || saving}
 className="w-full px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
 {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
 {t('recording.analyse.sollAssign') || 'Zuweisen'}
 </button>
 </div>
 </div>
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════
// TAB 5: ONBOARDING
// ══════════════════════════════════════════════════════════════════

function OnboardingTab({ t, stats }) {
 const [onboardings, setOnboardings] = useState([]);
 const [obStats, setObStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [showCreate, setShowCreate] = useState(false);
 const [detailOb, setDetailOb] = useState(null);
 const [filterStatus, setFilterStatus] = useState('alle');
 const [toast, setToast] = useState(null);

 const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

 const loadData = useCallback(() => {
 setLoading(true);
 Promise.all([
 fetch(`${API}/api/recordings/onboarding?status=${filterStatus}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 fetch(`${API}/api/recordings/onboarding/stats`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 ]).then(([ob, st]) => {
 if (ob) setOnboardings(Array.isArray(ob.data) ? ob.data : Array.isArray(ob) ? ob : []);
 if (st) setObStats(st);
 }).catch(() => {}).finally(() => setLoading(false));
 }, [filterStatus]);

 useEffect(() => { loadData(); }, [loadData]);

 const handleAction = async (id, action) => {
 if (action === 'abbrechen') {
 if (!confirm(t('recording.onboarding.cancelConfirm') || 'Onboarding wirklich abbrechen?')) return;
 const res = await fetch(`${API}/api/recordings/onboarding/${id}`, { method: 'DELETE', credentials: 'include' });
 if (res.ok) { showToast('Abgebrochen'); loadData(); } else showToast('Fehler', 'error');
 } else if (action === 'pausieren') {
 const res = await fetch(`${API}/api/recordings/onboarding/${id}`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ status: 'pausiert' }),
 });
 if (res.ok) { showToast('Pausiert'); loadData(); } else showToast('Fehler', 'error');
 } else if (action === 'fortsetzen') {
 const res = await fetch(`${API}/api/recordings/onboarding/${id}`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ status: 'aktiv' }),
 });
 if (res.ok) { showToast('Fortgesetzt'); loadData(); } else showToast('Fehler', 'error');
 }
 };

 const aktiv = onboardings.filter(o => o.status === 'aktiv' || o.status === 'pausiert');
 const abgeschlossen = onboardings.filter(o => o.status === 'ziel_erreicht' || o.status === 'abgebrochen');

 return (
 <div className="space-y-6">
 {/* KPI Cards */}
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
 <KpiCard label={t('recording.onboarding.stats.activeCount') || 'Aktiv'} value={obStats?.aktiv ?? 0} icon={GraduationCap} color="bg-[#EBF4FD] text-[#338edc]" />
 <KpiCard label={t('recording.onboarding.stats.completedWeek') || 'Abgeschlossen (Woche)'} value={obStats?.abgeschlossen_woche ?? 0} icon={CheckCircle2} color="bg-[#ECFDF5] text-[#10B981]" />
 <KpiCard label={t('recording.onboarding.stats.avgProgress') || 'Ø Fortschritt'} value={obStats?.avg_fortschritt != null ? `${obStats.avg_fortschritt}%` : '—'} icon={TrendingUp} color="bg-[#ECFEFF] text-[#22D3EE]" />
 <KpiCard label={t('recording.onboarding.stats.avgDaysToGoal') || 'Ø Tage bis Ziel'} value={obStats?.avg_tage_bis_ziel != null ? obStats.avg_tage_bis_ziel : '—'} icon={Target} color="bg-[#FFFBEB] text-[#F59E0B]" />
 </div>

 {/* Actions */}
 <div className="flex items-center gap-3">
 <button onClick={() => setShowCreate(true)}
 className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors">
 <Plus className="w-4 h-4" /> {t('recording.onboarding.start') || 'Onboarding starten'}
 </button>
 <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
 className="px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]">
 <option value="alle">Alle</option>
 <option value="aktiv">Aktiv</option>
 <option value="ziel_erreicht">Abgeschlossen</option>
 </select>
 </div>

 {/* Aktive Onboardings */}
 {aktiv.length > 0 && (
 <div className="space-y-3">
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">
 {t('recording.onboarding.active') || 'Aktive Onboardings'}
 </h3>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
 {aktiv.map(ob => (
 <OnboardingCard key={ob.id} ob={ob} t={t} onAction={handleAction} onDetail={() => setDetailOb(ob)} showToast={showToast} />
 ))}
 </div>
 </div>
 )}

 {/* Abgeschlossene */}
 {abgeschlossen.length > 0 && (
 <details className="group">
 <summary className="cursor-pointer text-sm font-bold text-[var(--text-primary,#1A1A2E)] flex items-center gap-2">
 <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
 {t('recording.onboarding.completed') || 'Abgeschlossen'} ({abgeschlossen.length})
 </summary>
 <div className="mt-3 bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] overflow-hidden">
 <table className="w-full text-left">
 <thead>
 <tr className="border-b border-[var(--border-card,#E5E7EB)]">
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Mitarbeiter</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Typ</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Start</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Ende</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Videos</th>
 <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Status</th>
 </tr>
 </thead>
 <tbody>
 {abgeschlossen.map(ob => (
 <tr key={ob.id} className="border-b border-[var(--border-card,#E5E7EB)] last:border-b-0" style={{ height: 48 }}>
 <td className="px-4 py-2 text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{ob.mitarbeiter_name || `MA #${ob.mitarbeiter_id}`}</td>
 <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold uppercase ${kategorieColor(ob.prozess_typ)}`}>{ob.prozess_typ}</span></td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{ob.start_datum}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)]">{ob.ziel_erreicht_datum || '—'}</td>
 <td className="px-4 py-2 text-sm text-[var(--text-secondary,#4B5563)] tabular-nums">{ob.analysierte_videos}</td>
 <td className="px-4 py-2">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold ${
 ob.status === 'ziel_erreicht' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-muted,#9CA3AF)]'
 }`}>{ob.status === 'ziel_erreicht' ? '✓ Erreicht' : 'Abgebrochen'}</span>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </details>
 )}

 {/* Loading / Empty */}
 {loading && !onboardings.length && (
 <div className="py-12 text-center"><div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" /></div>
 )}
 {!loading && !onboardings.length && (
 <div className="py-16 text-center">
 <GraduationCap className="w-10 h-10 mx-auto mb-3 text-[var(--text-muted,#9CA3AF)]" />
 <p className="text-sm text-[var(--text-tertiary,#6B7280)]">Noch keine Onboardings gestartet</p>
 </div>
 )}

 {/* Modals */}
 {showCreate && <OnboardingCreateModal t={t} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); loadData(); }} showToast={showToast} />}
 {detailOb && <OnboardingDetailModal t={t} ob={detailOb} onClose={() => setDetailOb(null)} />}

 {toast && (
 <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-sm ${
 toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
 }`}>{toast.msg}</div>
 )}
 </div>
 );
}

// ── Onboarding Card ──
function OnboardingCard({ ob, t, onAction, onDetail, showToast }) {
 const fortschritt = ob.ziel_tage_unter_soll > 0
 ? Math.min(100, Math.round(ob.aktuelle_serie_unter_soll / ob.ziel_tage_unter_soll * 100)) : 0;
 const isPaused = ob.status === 'pausiert';
 const [urlCopied, setUrlCopied] = useState(false);

 const handleCopyDisplayUrl = async () => {
 if (!ob.camera_id) { showToast?.('Keine Kamera zugeordnet', 'error'); return; }
 try {
 const res = await fetch(`${API}/api/recordings/onboarding/display/${encodeURIComponent(ob.camera_id)}`, {
 credentials: 'include',
 headers: { 'x-agent-key': '' }, // Will be validated server-side
 });
 if (res.ok) {
 const data = await res.json();
 await navigator.clipboard.writeText(data.display_url);
 setUrlCopied(true);
 showToast?.(t('recording.onboarding.displayUrlCopied') || 'URL kopiert!');
 setTimeout(() => setUrlCopied(false), 3000);
 } else {
 showToast?.('Fehler beim Generieren der URL', 'error');
 }
 } catch { showToast?.('Fehler', 'error'); }
 };

 return (
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-5">
 <div className="flex items-start justify-between mb-3">
 <div>
 <h4 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">
 {ob.mitarbeiter_name || `MA #${ob.mitarbeiter_id}`}
 </h4>
 <div className="flex items-center gap-2 mt-1">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold uppercase ${kategorieColor(ob.prozess_typ)}`}>{ob.prozess_typ}</span>
 {isPaused && <span className="inline-flex px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">Pausiert</span>}
 </div>
 </div>
 <div className="flex items-center gap-2">
 <button onClick={handleCopyDisplayUrl} title={t('recording.onboarding.displayUrl') || 'Display-URL kopieren'}
 className={`p-1.5 rounded-lg text-[11px] font-medium transition-colors ${urlCopied ? 'bg-emerald-50 text-emerald-600' : 'text-[var(--text-tertiary)] hover:bg-[var(--bg-inset)]'}`}>
 {urlCopied ? <CheckCircle2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
 </button>
 <button onClick={onDetail}
 className="text-xs font-medium text-[#338edc] hover:text-[#2b7bc4] transition-colors">
 {t('recording.onboarding.details') || 'Details'}
 </button>
 </div>
 </div>

 {/* Fortschrittsbalken */}
 <div className="mb-3">
 <div className="flex items-center justify-between text-[11px] text-[var(--text-tertiary,#6B7280)] mb-1">
 <span>{t('recording.onboarding.serie') || 'Serie'}: {ob.aktuelle_serie_unter_soll} / {ob.ziel_tage_unter_soll} Tage</span>
 <span>{fortschritt}%</span>
 </div>
 <div className="h-2 rounded-full bg-[var(--bg-inset,#F8F9FC)] overflow-hidden">
 <div className="h-full rounded-full bg-[#338edc] transition-all" style={{ width: `${fortschritt}%` }} />
 </div>
 </div>

 {/* Stats */}
 <div className="grid grid-cols-3 gap-3 mb-3">
 <div>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{t('recording.onboarding.avgZeit') || 'Ø Zeit'}</p>
 <p className="text-sm font-bold text-[var(--text-primary,#1A1A2E)] tabular-nums">
 {ob.durchschnitt_zeit_sek ? formatDuration(ob.durchschnitt_zeit_sek) : '—'}
 </p>
 </div>
 <div>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{t('recording.onboarding.fehlerquote') || 'Fehlerquote'}</p>
 <p className="text-sm font-bold text-[var(--text-primary,#1A1A2E)] tabular-nums">
 {ob.aktuelle_fehlerquote != null ? `${ob.aktuelle_fehlerquote}%` : '—'}
 </p>
 </div>
 <div>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">Videos</p>
 <p className="text-sm font-bold text-[var(--text-primary,#1A1A2E)] tabular-nums">{ob.analysierte_videos}</p>
 </div>
 </div>

 {/* Actions */}
 <div className="flex items-center gap-2 pt-3 border-t border-[var(--border-card,#E5E7EB)]">
 <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] flex-1">
 {t('recording.onboarding.startDatum') || 'Gestartet'}: {ob.start_datum}
 </span>
 {isPaused ? (
 <button onClick={() => onAction(ob.id, 'fortsetzen')}
 className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#338edc] border border-[#338edc]/30 hover:bg-[#338edc]/5 transition-colors">
 {t('recording.onboarding.resume') || 'Fortsetzen'}
 </button>
 ) : (
 <button onClick={() => onAction(ob.id, 'pausieren')}
 className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-amber-600 border border-amber-300 hover:bg-amber-50 transition-colors">
 {t('recording.onboarding.pause') || 'Pausieren'}
 </button>
 )}
 <button onClick={() => onAction(ob.id, 'abbrechen')}
 className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-red-500 border border-red-200 hover:bg-red-50 transition-colors">
 {t('recording.onboarding.cancel') || 'Abbrechen'}
 </button>
 </div>
 </div>
 );
}

// ── Onboarding Create Modal ──
function OnboardingCreateModal({ t, onClose, onDone, showToast }) {
 const [mitarbeiter, setMitarbeiter] = useState([]);
 const [referenz, setReferenz] = useState([]);
 const [form, setForm] = useState({ mitarbeiter_id: '', prozess_typ: 'pack', soll_video_id: '', ziel_tage: 5, ziel_fehler: 5 });
 const [saving, setSaving] = useState(false);

 useEffect(() => {
 Promise.all([
 fetch(`${API}/api/mitarbeiter`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 fetch(`${API}/api/recordings/referenz`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
 ]).then(([ma, ref]) => {
 setMitarbeiter((Array.isArray(ma?.data) ? ma.data : Array.isArray(ma) ? ma : []).filter(m => m.aktiv !== false));
 setReferenz(ref || { data: [], soll_map: {} });
 }).catch(() => {});
 }, []);

 const handleSubmit = async () => {
 if (!form.mitarbeiter_id) return showToast('Mitarbeiter auswählen', 'error');
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/recordings/onboarding`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({
 mitarbeiter_id: parseInt(form.mitarbeiter_id),
 prozess_typ: form.prozess_typ,
 soll_video_id: form.soll_video_id ? parseInt(form.soll_video_id) : null,
 ziel_tage_unter_soll: parseInt(form.ziel_tage),
 ziel_fehlerquote_pct: parseFloat(form.ziel_fehler),
 }),
 });
 if (res.ok) { showToast('Onboarding gestartet'); onDone(); }
 else { const d = await res.json(); showToast(d.error || 'Fehler', 'error'); }
 } catch { showToast('Fehler', 'error'); }
 setSaving(false);
 };

 const sollVideos = (referenz.data || []).filter(r => {
 const sollMap = referenz.soll_map || {};
 return sollMap[form.prozess_typ] === r.id;
 });
 const allRefs = referenz.data || [];

 const inputCls = 'w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]';
 const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1.5';

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between mb-5">
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.onboarding.start') || 'Onboarding starten'}</h3>
 <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]"><X className="w-5 h-5" /></button>
 </div>

 <div className="space-y-4">
 <div>
 <label className={labelCls}>{t('recording.onboarding.selectMitarbeiter') || 'Mitarbeiter'}</label>
 <select value={form.mitarbeiter_id} onChange={e => setForm(f => ({ ...f, mitarbeiter_id: e.target.value }))} className={inputCls}>
 <option value="">{t('recording.onboarding.selectMitarbeiter') || 'Mitarbeiter auswählen...'}</option>
 {mitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
 </select>
 </div>
 <div>
 <label className={labelCls}>{t('recording.onboarding.selectProzessTyp') || 'Prozess-Typ'}</label>
 <select value={form.prozess_typ} onChange={e => setForm(f => ({ ...f, prozess_typ: e.target.value }))} className={inputCls}>
 <option value="pack">Pack</option>
 <option value="inbound">Inbound</option>
 <option value="retoure">Retoure</option>
 </select>
 </div>
 <div>
 <label className={labelCls}>{t('recording.onboarding.selectSollVideo') || 'SOLL-Video'}</label>
 <select value={form.soll_video_id} onChange={e => setForm(f => ({ ...f, soll_video_id: e.target.value }))} className={inputCls}>
 <option value="">Kein SOLL-Video</option>
 {allRefs.map(r => <option key={r.id} value={r.id}>{r.auftrag_id} ({formatDuration(r.dauer_sekunden)})</option>)}
 </select>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className={labelCls}>{t('recording.onboarding.zielTage') || 'Ziel: Tage unter SOLL'}</label>
 <input type="number" value={form.ziel_tage} onChange={e => setForm(f => ({ ...f, ziel_tage: e.target.value }))}
 min={1} max={30} className={inputCls} />
 </div>
 <div>
 <label className={labelCls}>{t('recording.onboarding.zielFehler') || 'Ziel: Max. Fehlerquote %'}</label>
 <input type="number" value={form.ziel_fehler} onChange={e => setForm(f => ({ ...f, ziel_fehler: e.target.value }))}
 min={0} max={100} step={0.5} className={inputCls} />
 </div>
 </div>
 </div>

 <button onClick={handleSubmit} disabled={saving}
 className="w-full mt-5 px-4 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
 {saving ? 'Wird gestartet...' : (t('recording.onboarding.start') || 'Onboarding starten')}
 </button>
 </div>
 </div>
 );
}

// ── Onboarding Detail Modal ──
function OnboardingDetailModal({ t, ob, onClose }) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetch(`${API}/api/recordings/onboarding/${ob.id}/fortschritt`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setData(d))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, [ob.id]);

 useEffect(() => {
 const esc = (e) => e.key === 'Escape' && onClose();
 document.addEventListener('keydown', esc);
 return () => document.removeEventListener('keydown', esc);
 }, [onClose]);

 // Deduplicate tips with frequency count
 const getFrequentTipps = (tipps) => {
 if (!tipps || !tipps.length) return [];
 const freq = {};
 tipps.forEach(tipp => {
 const key = tipp.trim().toLowerCase();
 if (!freq[key]) freq[key] = { text: tipp, count: 0 };
 freq[key].count++;
 });
 return Object.values(freq).sort((a, b) => b.count - a.count).slice(0, 5);
 };

 // Compute stat cards from tages_verlauf
 const computeStats = (tv, analysen) => {
 if (!tv || !tv.length) return null;
 const withScore = tv.filter(d => d.avg_score != null && parseFloat(d.avg_score) > 0);
 if (!withScore.length) return null;
 const best = withScore.reduce((a, b) => parseFloat(a.avg_score) > parseFloat(b.avg_score) ? a : b);
 const worst = withScore.reduce((a, b) => parseFloat(a.avg_score) < parseFloat(b.avg_score) ? a : b);
 const first = withScore[0];
 const last = withScore[withScore.length - 1];
 const firstZeit = parseFloat(first.avg_zeit) || 0;
 const lastZeit = parseFloat(last.avg_zeit) || 0;
 const verbesserung = firstZeit > 0 ? Math.round((1 - lastZeit / firstZeit) * 100) : 0;
 return {
 total_videos: analysen?.length || ob.analysierte_videos || 0,
 bester_tag: { datum: best.datum, score: parseFloat(best.avg_score).toFixed(1) },
 schlechtester_tag: { datum: worst.datum, score: parseFloat(worst.avg_score).toFixed(1) },
 verbesserung,
 };
 };

 const frequentTipps = data ? getFrequentTipps(data.tipps || []) : [];
 const stats = data ? computeStats(data.tages_verlauf, data.letzte_analysen) : null;

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card)]">
 <div>
 <h3 className="text-base font-bold text-[var(--text-primary,#1A1A2E)]">
 {ob.mitarbeiter_name || `MA #${ob.mitarbeiter_id}`} — {t('recording.onboarding.fortschritt') || 'Fortschritt'}
 </h3>
 <p className="text-xs text-[var(--text-tertiary)] mt-0.5">{ob.prozess_typ} | Seit {ob.start_datum}</p>
 </div>
 <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)]"><X className="w-5 h-5" /></button>
 </div>
 <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(85vh - 80px)' }}>
 {loading ? (
 <div className="py-8 text-center"><div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" /></div>
 ) : data ? (
 <div className="space-y-6">
 {/* Statistik-Cards */}
 {stats && (
 <div className="grid grid-cols-4 gap-3">
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-xl px-3 py-2.5 text-center">
 <p className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] tabular-nums">{stats.total_videos}</p>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">Videos analysiert</p>
 </div>
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-xl px-3 py-2.5 text-center">
 <p className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] tabular-nums">{stats.bester_tag.score}</p>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{t('recording.onboarding.besterTag') || 'Bester Tag'}</p>
 <p className="text-[10px] text-[var(--text-muted,#9CA3AF)]">{new Date(stats.bester_tag.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })}</p>
 </div>
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-xl px-3 py-2.5 text-center">
 <p className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] tabular-nums">{stats.schlechtester_tag.score}</p>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{t('recording.onboarding.schlechtesterTag') || 'Schlechtester Tag'}</p>
 <p className="text-[10px] text-[var(--text-muted,#9CA3AF)]">{new Date(stats.schlechtester_tag.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })}</p>
 </div>
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-xl px-3 py-2.5 text-center">
 <p className={`text-lg font-bold tabular-nums ${stats.verbesserung > 0 ? 'text-emerald-600' : stats.verbesserung < 0 ? 'text-red-500' : 'text-[var(--text-primary,#1A1A2E)]'}`}>
 {stats.verbesserung > 0 ? '+' : ''}{stats.verbesserung}%
 </p>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{t('recording.onboarding.verbesserung') || 'Verbesserung'}</p>
 </div>
 </div>
 )}

 {/* Tages-Verlauf Chart — Dual Y-Axis */}
 {(data.tages_verlauf || []).length > 0 && (
 <div>
 <h4 className="text-sm font-bold text-[var(--text-primary)] mb-3">{t('recording.onboarding.tagesVerlauf') || 'Tages-Verlauf'}</h4>
 <ResponsiveContainer width="100%" height={200}>
 <LineChart data={(data.tages_verlauf || []).map(d => ({
 ...d,
 datum: new Date(d.datum).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }),
 avg_score: parseFloat(d.avg_score) || 0,
 avg_zeit: parseInt(d.avg_zeit) || 0,
 }))}>
 <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
 <XAxis dataKey="datum" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
 <YAxis yAxisId="zeit" orientation="left" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} label={{ value: 'Sek', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#6B7280' } }} />
 <YAxis yAxisId="score" orientation="right" domain={[0, 10]} tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} label={{ value: 'Score', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#6B7280' } }} />
 <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)' }} />
 <Line yAxisId="zeit" type="monotone" dataKey="avg_zeit" stroke="#338edc" strokeWidth={2} name="Ø Zeit (s)" dot={{ r: 3, fill: '#338edc' }} />
 <Line yAxisId="score" type="monotone" dataKey="avg_score" stroke="#10B981" strokeWidth={2} name="Ø Score" dot={{ r: 3, fill: '#10B981' }} />
 {data.soll_zeit_sek && (
 <>
 <ReferenceLine yAxisId="zeit" y={data.soll_zeit_sek} stroke="#E5E7EB" strokeDasharray="5 5" label={{ value: 'SOLL', position: 'right', fill: '#9CA3AF', fontSize: 10 }} />
 <Area yAxisId="zeit" type="monotone" dataKey={() => data.soll_zeit_sek} fill="#10B981" fillOpacity={0.05} stroke="none" />
 </>
 )}
 </LineChart>
 </ResponsiveContainer>
 </div>
 )}

 {/* Letzte Analysen */}
 {(data.letzte_analysen || []).length > 0 && (
 <div>
 <h4 className="text-sm font-bold text-[var(--text-primary)] mb-3">Letzte Analysen</h4>
 <div className="space-y-2">
 {(data.letzte_analysen || []).map(a => (
 <div key={a.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)]">
 <span className={`inline-flex px-2 py-0.5 rounded-lg text-[12px] font-bold tabular-nums ${scoreColor(a.gesamtbewertung)}`}>
 {a.gesamtbewertung}
 </span>
 <span className="text-sm font-medium text-[var(--text-primary)]">{a.auftrag_id}</span>
 <span className="text-xs text-[var(--text-tertiary)] flex-1 truncate">{a.zusammenfassung || '—'}</span>
 <span className="text-xs text-[var(--text-tertiary)]">{formatDuration(a.dauer_sekunden)}</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Häufigste Tipps (dedupliziert mit Frequenz) */}
 {frequentTipps.length > 0 && (
 <div>
 <h4 className="text-sm font-bold text-[var(--text-primary)] mb-3">{t('recording.onboarding.haeufigsteTipps') || 'Häufigste Tipps'}</h4>
 <div className="space-y-2">
 {frequentTipps.map((item, i) => (
 <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
 <Zap className="w-4 h-4 text-[#338edc] flex-shrink-0 mt-0.5" />
 <p className="text-sm text-[var(--text-secondary,#4B5563)] flex-1">{item.text}</p>
 {item.count > 1 && (
 <span className="inline-flex px-1.5 py-0.5 rounded-lg text-[10px] font-bold text-[#338edc] bg-[#338edc]/10">{item.count}×</span>
 )}
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 ) : (
 <p className="text-center text-sm text-[var(--text-tertiary)]">Keine Daten</p>
 )}
 </div>
 </div>
 </div>
 );
}

// ── Prozess-Zuordnung Component ──

function ProzessZuordnung({ config, set, t, selectCls, labelCls }) {
 const [prozesse, setProzesse] = useState([]);
 useEffect(() => {
 fetch(`${API}/api/prozesse`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setProzesse(Array.isArray(d?.prozesse) ? d.prozesse : Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []))
 .catch(() => {});
 }, []);

 const kategorien = [
 { key: 'recording_prozess_pack', label: 'Pack' },
 { key: 'recording_prozess_inbound', label: 'Inbound' },
 { key: 'recording_prozess_retoure', label: 'Retoure' },
 ];

 return (
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {kategorien.map(kat => (
 <div key={kat.key}>
 <label className={labelCls}>{kat.label}</label>
 <select value={config[kat.key] || ''} onChange={e => set(kat.key, e.target.value)} className={selectCls}>
 <option value="">{t('recording.analyse.prozessSelect') || 'Prozess auswählen...'}</option>
 {prozesse.map(p => <option key={p.id} value={String(p.id)}>{p.titel}</option>)}
 </select>
 </div>
 ))}
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════
// TAB 6: EINSTELLUNGEN
// ══════════════════════════════════════════════════════════════════

function SettingsTab({ t }) {
 const [config, setConfig] = useState({});
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [toast, setToast] = useState(null);
 const [dirty, setDirty] = useState(false);
 const [showPw, setShowPw] = useState({});

 useEffect(() => {
 fetch(`${API}/api/recordings/config`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => {
 const map = {};
 for (const row of (d?.data || [])) {
 // system_config stores JSONB values — unwrap strings
 let v = row.value;
 if (typeof v === 'string') {
 try { v = JSON.parse(v); } catch { /* keep as-is */ }
 }
 map[row.key] = v;
 }
 setConfig(map);
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const showToastMsg = (msg, type = 'success') => {
 setToast({ msg, type });
 setTimeout(() => setToast(null), 3000);
 };

 const set = (key, value) => {
 setConfig(c => ({ ...c, [key]: value }));
 setDirty(true);
 };

 const generateApiKey = () => {
 set('recording_agent_api_key', crypto.randomUUID());
 };

 const handleSave = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/recordings/config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(config),
 });
 if (res.ok) { showToastMsg(t('recording.config.saved') || 'Einstellungen gespeichert'); setDirty(false); }
 else showToastMsg(t('recording.config.saveError') || 'Fehler beim Speichern', 'error');
 } catch { showToastMsg(t('recording.config.saveError') || 'Fehler beim Speichern', 'error'); }
 setSaving(false);
 };

 const togglePw = (key) => setShowPw(s => ({ ...s, [key]: !s[key] }));

 if (loading) {
 return (
 <div className="py-12 text-center text-[var(--text-tertiary,#6B7280)]">
 <div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 );
 }

 const inputCls = 'w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] placeholder:text-[var(--text-muted,#9CA3AF)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/30';
 const selectCls = 'w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]';
 const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-1.5';
 const disabledInputCls = 'w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-muted,#9CA3AF)] cursor-not-allowed';
 const disabledSelectCls = 'w-full px-3 py-2.5 text-sm rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-muted,#9CA3AF)] cursor-not-allowed';

 const PwField = ({ label, configKey, placeholder, disabled }) => (
 <div>
 <label className={labelCls}>{label}</label>
 <div className="relative">
 <input type={showPw[configKey] ? 'text' : 'password'} value={config[configKey] || ''} placeholder={placeholder}
 disabled={disabled} onChange={e => set(configKey, e.target.value)}
 className={disabled ? disabledInputCls : inputCls} />
 {!disabled && (
 <button type="button" onClick={() => togglePw(configKey)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary)]">
 {showPw[configKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </button>
 )}
 </div>
 </div>
 );

 const NumberField = ({ label, configKey, suffix, disabled }) => (
 <div>
 <label className={labelCls}>{label}</label>
 <div className="relative">
 <input type="number" value={config[configKey] || ''} disabled={disabled}
 onChange={e => set(configKey, e.target.value)}
 className={disabled ? disabledInputCls : inputCls}
 style={suffix ? { paddingRight: 52 } : undefined} />
 {suffix && (
 <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-medium text-[var(--text-tertiary,#6B7280)]">{suffix}</span>
 )}
 </div>
 </div>
 );

 return (
 <div className="space-y-6">
 {/* Card 1: Recording-Agent */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-5">
 <Server className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.config.agent') || 'Recording-Agent'}</h3>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className={labelCls}>{t('recording.config.agentUrl') || 'Agent-URL'}</label>
 <input type="text" value={config.recording_agent_url || ''} placeholder="http://192.168.1.200:3010"
 onChange={e => set('recording_agent_url', e.target.value)} className={inputCls} />
 </div>
 <div>
 <label className={labelCls}>{t('recording.config.agentApiKey') || 'Agent-API-Key'}</label>
 <div className="flex gap-2">
 <div className="relative flex-1">
 <input type={showPw.agent_key ? 'text' : 'password'} value={config.recording_agent_api_key || ''}
 onChange={e => set('recording_agent_api_key', e.target.value)} className={inputCls} />
 <button type="button" onClick={() => togglePw('agent_key')}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-secondary)]">
 {showPw.agent_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
 </button>
 </div>
 <button type="button" onClick={generateApiKey}
 className="flex items-center gap-1.5 px-3 py-2.5 rounded-[10px] text-xs font-medium border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors whitespace-nowrap">
 <KeyRound className="w-3.5 h-3.5" />
 {t('recording.config.generate') || 'Generieren'}
 </button>
 </div>
 </div>
 <div>
 <label className={labelCls}>{t('recording.config.encodingCodec') || 'Encoding-Codec'}</label>
 <select value={config.recording_encoding_codec || 'hevc_vaapi'} onChange={e => set('recording_encoding_codec', e.target.value)} className={selectCls}>
 <option value="hevc_vaapi">hevc_vaapi</option>
 <option value="libx265">libx265</option>
 </select>
 <p className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">
 {t('recording.config.encodingCodecHint') || 'hevc_vaapi = Hardware (NUC), libx265 = Software (VM)'}
 </p>
 </div>
 <div>
 <label className={labelCls}>{t('recording.config.encodingQuality') || 'Qualität'}</label>
 <select value={config.recording_encoding_quality || '720p'} onChange={e => set('recording_encoding_quality', e.target.value)} className={selectCls}>
 <option value="720p">720p</option>
 <option value="1080p">1080p</option>
 </select>
 </div>
 </div>
 </div>

 {/* Card 2: Cloud-Speicher (Hetzner) */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-5">
 <HardDrive className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.config.hetzner') || 'Cloud-Speicher (Hetzner)'}</h3>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className={labelCls}>{t('recording.config.hetznerUrl') || 'API-URL'}</label>
 <input type="text" value={config.recording_hetzner_api_url || ''} placeholder="https://video.weship.eu"
 onChange={e => set('recording_hetzner_api_url', e.target.value)} className={inputCls} />
 </div>
 <PwField label={t('recording.config.hetznerApiKey') || 'API-Key'} configKey="recording_hetzner_api_key" />
 <NumberField label={t('recording.config.retentionDays') || 'Aufbewahrungsdauer'} configKey="recording_retention_days" suffix={t('recording.config.retentionDaysSuffix') || 'Tage'} />
 <NumberField label={t('recording.config.maxDurationPack') || 'Max. Dauer Pack'} configKey="recording_max_duration_pack" suffix={t('recording.config.secondsSuffix') || 'Sek.'} />
 <NumberField label={t('recording.config.maxDurationRetoure') || 'Max. Dauer Retoure'} configKey="recording_max_duration_retoure" suffix={t('recording.config.secondsSuffix') || 'Sek.'} />
 <NumberField label={t('recording.config.maxDurationInbound') || 'Max. Dauer Wareneingang'} configKey="recording_max_duration_inbound" suffix={t('recording.config.secondsSuffix') || 'Sek.'} />
 </div>
 </div>

 {/* Card 3: AI-Analyse */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-5">
 <BarChart3 className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.config.aiTitle') || 'AI-Analyse'}</h3>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className={labelCls}>{t('recording.config.analyseEnabled') || 'AI-Analyse aktivieren'}</label>
 <label className="flex items-center gap-2 text-sm text-[var(--text-primary,#1A1A2E)] cursor-pointer">
 <input type="checkbox" checked={config.recording_analyse_enabled === 'true' || config.recording_analyse_enabled === true}
 onChange={e => set('recording_analyse_enabled', e.target.checked ? 'true' : 'false')}
 className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)]" />
 {t('recording.config.analyseEnabled') || 'Aktiviert'}
 </label>
 </div>
 <div>
 <label className={labelCls}>{t('recording.config.analyseModus') || 'Analyse-Modus'}</label>
 <select value={config.recording_analyse_modus || 'stichprobe'} onChange={e => set('recording_analyse_modus', e.target.value)} className={selectCls}>
 <option value="stichprobe">Stichprobe</option>
 <option value="ausreisser">Ausreißer</option>
 <option value="alle">Alle</option>
 </select>
 </div>
 <NumberField label={t('recording.config.stichprobeRate') || 'Stichprobe-Rate'} configKey="recording_stichprobe_rate" />
 <NumberField label={t('recording.config.ausreisserFaktor') || 'Ausreißer-Faktor'} configKey="recording_ausreisser_faktor" suffix="×" />
 <PwField label={t('recording.config.geminiApiKey') || 'Gemini API Key'} configKey="gemini_api_key" />
 <div>
 <label className={labelCls}>{t('recording.config.geminiModel') || 'Gemini Modell'}</label>
 <select value={config.gemini_model || 'gemini-2.5-pro'} onChange={e => set('gemini_model', e.target.value)} className={selectCls}>
 <option value="gemini-2.5-pro">gemini-2.5-pro</option>
 <option value="gemini-2.5-flash">gemini-2.5-flash</option>
 </select>
 </div>
 </div>
 {/* Prozess-Zuordnung */}
 <div className="mt-5 pt-5 border-t border-[var(--border-card,#E5E7EB)]">
 <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-3">{t('recording.analyse.prozessZuordnung') || 'Prozess-Zuordnung'}</h4>
 <ProzessZuordnung config={config} set={set} t={t} selectCls={selectCls} labelCls={labelCls} />
 </div>
 </div>

 {/* Card 4: Onboarding */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-5">
 <GraduationCap className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.config.onboarding') || 'Onboarding'}</h3>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className={labelCls}>{t('recording.config.onboardingEnabled') || 'Onboarding aktivieren'}</label>
 <label className="flex items-center gap-2 text-sm text-[var(--text-secondary,#4B5563)] cursor-pointer">
 <input type="checkbox" checked={config.recording_onboarding_enabled === 'true' || config.recording_onboarding_enabled === true}
 onChange={e => set('recording_onboarding_enabled', e.target.checked ? 'true' : 'false')}
 className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)]" />
 {t('recording.config.onboardingEnabled') || 'Aktiviert'}
 </label>
 </div>
 <NumberField label={t('recording.config.onboardingZielTage') || 'Ziel-Tage unter SOLL'} configKey="recording_onboarding_ziel_tage" />
 <NumberField label={t('recording.config.onboardingZielFehler') || 'Max. Fehlerquote'} configKey="recording_onboarding_ziel_fehler" suffix="%" />
 </div>
 </div>

 {/* Card 5: Display-Konfiguration */}
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[12px] p-6">
 <div className="flex items-center gap-3 mb-5">
 <Maximize2 className="w-5 h-5 text-[var(--text-tertiary,#6B7280)]" />
 <h3 className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">{t('recording.onboarding.displayConfig') || 'Display-Konfiguration'}</h3>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <NumberField label={t('recording.onboarding.ergebnisAnzeigedauer') || 'Ergebnis-Anzeigedauer'} configKey="recording_display_result_seconds" suffix="Sek" />
 <div>
 <label className={labelCls}>{t('recording.onboarding.konfetti') || 'Konfetti bei Zielerreichung'}</label>
 <label className="flex items-center gap-2 text-sm text-[var(--text-secondary,#4B5563)] cursor-pointer">
 <input type="checkbox" checked={config.recording_display_confetti === 'true' || config.recording_display_confetti === true}
 onChange={e => set('recording_display_confetti', e.target.checked ? 'true' : 'false')}
 className="w-4 h-4 rounded border-[var(--border-card,#E5E7EB)]" />
 Aktiv
 </label>
 </div>
 <NumberField label={t('recording.onboarding.tokenGueltigkeit') || 'Token-Gültigkeit'} configKey="recording_display_token_days" suffix="Tage" />
 </div>
 </div>

 {/* Save Button */}
 <div className="flex justify-end">
 <button onClick={handleSave} disabled={saving || !dirty}
 className="flex items-center gap-2 px-6 py-2.5 rounded-[10px] bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
 {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
 {t('common.save') || 'Speichern'}
 </button>
 </div>

 {/* Toast */}
 {toast && (
 <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-sm ${
 toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
 }`}>
 {toast.msg}
 </div>
 )}
 </div>
 );
}
